// ============================================
// 🎯 SALES CONTEXT
// ============================================
// Manages sales, quotations, and invoices with auto-numbering

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useDocumentNumbering } from '@/app/hooks/useDocumentNumbering';
import { useAccountingOptional } from '@/app/context/AccountingContext';
import { useSupabase } from '@/app/context/SupabaseContext';
import {
  saleService,
  Sale as SupabaseSale,
  SaleItem as SupabaseSaleItem,
  isSalesInvoiceNoUniqueViolation,
} from '@/app/services/saleService';
import { productService } from '@/app/services/productService';
import { branchService } from '@/app/services/branchService';
import { comboService } from '@/app/services/comboService';
import { useSettings } from '@/app/context/SettingsContext';
import { useFormatCurrency } from '@/app/hooks/useFormatCurrency';
import { toast } from 'sonner';
import { activityLogService } from '@/app/services/activityLogService';
import { documentNumberService } from '@/app/services/documentNumberService';
import { shipmentService } from '@/app/services/shipmentService';
import {
  canPostAccountingForSaleStatus,
  canPostStockForSaleStatus,
} from '@/app/lib/postingStatusGate';
import { getSaleDisplayNumber, isPreFinalSaleDocumentNo } from '@/app/lib/documentDisplayNumbers';
import { getCurrentLocalTimestamp, localNowDateString } from '@/app/utils/localDate';
import { perfStart } from '@/app/utils/perfTiming';
import { readSaleBillRef } from '@/app/utils/saleBillRef';
import {
  parseCustomizationDetails,
  deriveBaseUnitPriceFromStored,
  resolveSaleLineUnitPrice,
  buildCustomizationDetailsForPersist,
  hasBespokeContent,
} from '@/app/types/bespoke';
import { insertSaleItemsOrdered } from '@/app/services/saleService';
import { assertDomainEditSafetyTestMode, classifySalesEdit } from '@/app/lib/accountingEditClassification';
import { createAccountingEditTraceId, pushAccountingEditTrace } from '@/app/lib/accountingEditTrace';
import {
  DATA_INVALIDATED_EVENT,
  dispatchSaleLifecycleInvalidated,
  shouldAcceptInvalidation,
  type DataInvalidationDetail,
} from '@/app/lib/dataInvalidationBus';
import { filterSaleLinesForStockPosting } from '@/app/lib/saleStockLineEligibility';
import {
  snapshotBespokeWorkOrderAnchors,
  relinkBespokeWorkOrdersAfterSaleItemReplace,
} from '@/app/services/bespokeWorkOrderRelinkService';
import { coerceUuidOrNull } from '@/app/utils/uuidCoerce';
import { normalizeVariationIdForPersist } from '@/app/utils/saleLineVariation';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isValidBranchId(id: string | null | undefined): id is string {
  return !!id && UUID_REGEX.test(id);
}

export { isValidBranchId };

/** Sale header `location` is branch UUID. Prefer it over context `branchId` (often `'all'` for admins). */
function branchIdFromSaleHeader(
  locationOrBranch: string | null | undefined,
  contextBranchId: string | null | undefined
): string | null {
  const fromSale = typeof locationOrBranch === 'string' ? locationOrBranch.trim() : '';
  if (isValidBranchId(fromSale)) return fromSale;
  if (isValidBranchId(contextBranchId ?? null)) return contextBranchId as string;
  return null;
}

type SaleChargeRow = {
  charge_type?: string;
  chargeType?: string;
  amount?: number;
  charged_to_customer?: boolean;
  tailor_contact_id?: string | null;
};

type SaleExtraExpenseInput = {
  type?: string;
  amount?: number;
  tailorExpenseCategoryId?: string | null;
  tailor_expense_category_id?: string | null;
  tailorContactId?: string | null;
  tailor_contact_id?: string | null;
};

function isChargeExtrasOnCustomerBill(saleData: { chargeExtrasToCustomer?: boolean }): boolean {
  return (saleData as { chargeExtrasToCustomer?: boolean }).chargeExtrasToCustomer !== false;
}

function sumChargedExtraExpenses(
  list: SaleExtraExpenseInput[] | undefined,
  chargeOnBill: boolean,
): number {
  if (!chargeOnBill || !Array.isArray(list)) return 0;
  return list.reduce((s, e) => s + (Number(e?.amount) || 0), 0);
}

async function buildSaleChargeRowsFromExtras(
  companyId: string,
  extraExpensesList: SaleExtraExpenseInput[] | undefined,
  chargeOnBill: boolean,
  shippingAmount: number,
  discountAmount: number,
): Promise<
  {
    charge_type: string;
    amount: number;
    ledger_account_id?: string | null;
    charged_to_customer?: boolean;
    tailor_contact_id?: string | null;
    expense_category_id?: string | null;
  }[]
> {
  const charges: {
    charge_type: string;
    amount: number;
    ledger_account_id?: string | null;
    charged_to_customer?: boolean;
    tailor_contact_id?: string | null;
    expense_category_id?: string | null;
  }[] = [];
  const hasExtraLines =
    Array.isArray(extraExpensesList) &&
    extraExpensesList.some((e) => Number(e?.amount ?? 0) > 0);
  let extraLedgerAccountId: string | null = null;
  if (hasExtraLines && companyId) {
    const { resolveExtraServiceIncomeAccountId } = await import('@/app/services/saleAccountingService');
    extraLedgerAccountId = await resolveExtraServiceIncomeAccountId(companyId);
  }
  if (Array.isArray(extraExpensesList)) {
    extraExpensesList.forEach((e) => {
      const amt = Number(e?.amount ?? 0);
      if (amt > 0) {
        charges.push({
          charge_type: (e?.type ?? 'other') as string,
          amount: amt,
          ledger_account_id: extraLedgerAccountId,
          charged_to_customer: chargeOnBill,
          tailor_contact_id: coerceUuidOrNull(e.tailorContactId ?? e.tailor_contact_id),
          expense_category_id: coerceUuidOrNull(
            e.tailorExpenseCategoryId ?? e.tailor_expense_category_id,
          ),
        });
      }
    });
  }
  if (shippingAmount > 0) {
    charges.push({ charge_type: 'shipping', amount: shippingAmount, charged_to_customer: true });
  }
  if (discountAmount > 0) {
    charges.push({ charge_type: 'discount', amount: discountAmount, charged_to_customer: true });
  }
  return charges;
}

function sumSaleChargeRows(
  charges: SaleChargeRow[] | undefined,
  predicate: (type: string) => boolean,
): number {
  if (!Array.isArray(charges)) return 0;
  return charges.reduce((sum, c) => {
    const type = String(c.charge_type ?? c.chargeType ?? '').toLowerCase();
    if (!predicate(type)) return sum;
    return sum + (Number(c.amount) || 0);
  }, 0);
}

/** Extra income on invoice (stitching etc.) — not shipping. */
function extraExpenseAmountFromSaleRow(supabaseSale: Record<string, unknown>): number {
  const charges = (supabaseSale.charges ?? supabaseSale.sale_charges) as SaleChargeRow[] | undefined;
  const fromCharges = sumSaleChargeRows(charges, (t) => t !== 'discount' && t !== 'shipping');
  if (fromCharges > 0) return fromCharges;
  if (supabaseSale.extra_expenses != null && Number(supabaseSale.extra_expenses) > 0) {
    return Number(supabaseSale.extra_expenses);
  }
  const shipment = Number(supabaseSale.shipment_charges ?? supabaseSale.shipping_charges ?? 0) || 0;
  const legacyExpenses = Number(supabaseSale.expenses ?? 0) || 0;
  if (legacyExpenses > 0 && shipment <= 0) return legacyExpenses;
  return 0;
}

function shippingChargeAmountFromSaleRow(supabaseSale: Record<string, unknown>): number {
  const charges = (supabaseSale.charges ?? supabaseSale.sale_charges) as SaleChargeRow[] | undefined;
  const fromCharges = sumSaleChargeRows(charges, (t) => t === 'shipping');
  if (fromCharges > 0) return fromCharges;
  return Number(supabaseSale.shipment_charges ?? supabaseSale.shipping_charges ?? 0) || 0;
}

// ============================================
// TYPES
// ============================================

export type PaymentStatus = 'paid' | 'partial' | 'unpaid';
export type ShippingStatus =
  | 'delivered'
  | 'processing'
  | 'pending'
  | 'cancelled'
  | 'Booked'
  | 'Picked'
  | 'In Transit'
  | 'Out for Delivery'
  | 'Delivered'
  | 'Returned'
  | 'Cancelled'
  | 'Dispatched'
  | 'Created'
  | 'Packed'
  | 'Pending';
export type SaleType = 'invoice' | 'quotation';

export interface SaleItem {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  quantity: number;
  price: number;
  /** Retail base before customization_charges (derived on load). */
  baseUnitPrice?: number;
  discount: number;
  tax: number;
  total: number;
  variationId?: string; // Backend variation id - for ledger/reporting/stock
  /** Unit from backend (sales_items.unit): piece, box, meters, etc. */
  unit?: string;
  /** Packing from backend (sales_items.packing_*) - for display/reports */
  packingDetails?: { packing_type?: string; packing_quantity?: number; packing_unit?: string; [k: string]: unknown };
  customizationDetails?: Record<string, unknown>;
  /** Fabric child line linked to parent generic SKU row. */
  bespokeParentItemId?: string | null;
  /** True for the auto-generated studio product line; false/undefined for material items (fabric, lace, etc.). */
  isStudioProduct?: boolean;
}

export interface Sale {
  id: string;
  /** Main list / header number — display-only; use getSaleDisplayNumber from row when rendering lists. */
  invoiceNo: string;
  /** Same-row lifecycle stage numbers (audit / history); optional on older rows. */
  draftNo?: string;
  quotationNo?: string;
  orderNo?: string;
  type: SaleType;
  status?: 'draft' | 'quotation' | 'order' | 'final' | 'cancelled'; // Sale lifecycle; cancelled = reversed
  customer: string;
  customerName: string;
  contactNumber: string;
  date: string;
  /** Branch display name for UI only — use branchId for DB writes. */
  location: string;
  /** Branch UUID from sales.branch_id — use for stock, WO, GL; never show in UI. */
  branchId?: string;
  items: SaleItem[];
  itemsCount: number;
  subtotal: number;
  discount: number;
  tax: number;
  expenses: number;
  shippingCharges?: number; // For backward compatibility
  otherCharges?: number; // Extra charges beyond shipping
  total: number;
  /** Studio worker cost (from linked productions). Final bill = total + studioCharges. */
  studioCharges?: number;
  paid: number;
  due: number;
  returnDue: number;
  paymentStatus: PaymentStatus;
  paymentMethod: string;
  shippingStatus: ShippingStatus | string;
  /** True when sale has at least one record in sale_shipments. */
  hasShipment?: boolean;
  /** First shipment id (from sale_shipments) for this sale; used for ledger and history. */
  firstShipmentId?: string;
  notes?: string;
  /** Manual customer bill / REF # (sales.customer_bill_ref). */
  customerBillRef?: string;
  /** Delivery/deadline date (YYYY-MM-DD) for studio sales and regular orders. */
  deadline?: string;
  attachments?: { url: string; name: string }[] | null; // Sale attachments
  /** Line-level charges from sale_charges (for view/drawer actual data) */
  charges?: Array<{ charge_type?: string; chargeType?: string; amount: number }>;
  createdAt: string;
  updatedAt: string;
  is_studio?: boolean;
  /** User who created the sale (full name for display) */
  createdBy?: string;
  /** Origin e.g. studio_production_v3 */
  source?: string;
  /** ID of source record (e.g. studio_production_orders_v3.id) */
  source_id?: string;
  /** Show production breakdown on invoice (Studio V3) */
  show_studio_breakdown?: boolean;
  /** Salesperson (user id) for commission */
  salesmanId?: string | null;
  /** Commission amount stored on sale (for period reporting) */
  commissionAmount?: number;
  /** pending | posted — only batch posting sets posted */
  commissionStatus?: 'pending' | 'posted';
  /** Set when posted via Commission Report */
  commissionBatchId?: string | null;
  /** Commission rate at time of sale (audit) */
  commissionPercent?: number | null;
}

interface SalesContextType {
  sales: Sale[];
  loading: boolean;
  /** Total count for pagination (when using paginated load). */
  totalCount: number;
  /** Current 0-based page (pageSize rows per page). */
  page: number;
  pageSize: number;
  /** Set current page and reload (0-based). */
  setPage: (page: number) => void;
  /** Set page size (rows per page) and reset to first page. Use -1 for All (capped fetch). */
  setPageSize: (size: number) => void;
  /** Server-side list search (debounced from SalesPage). */
  listSearch: string;
  setListSearch: (q: string) => void;
  getSaleById: (id: string) => Sale | undefined;
  createSale: (sale: Omit<Sale, 'id' | 'invoiceNo' | 'createdAt' | 'updatedAt'>) => Promise<Sale>;
  updateSale: (id: string, updates: Partial<Sale>) => Promise<void>;
  deleteSale: (id: string) => Promise<void>;
  recordPayment: (saleId: string, amount: number, method: string, accountId?: string) => Promise<void>;
  updateShippingStatus: (saleId: string, status: ShippingStatus) => Promise<void>;
  convertQuotationToInvoice: (quotationId: string) => Promise<Sale>;
  refreshSales: () => Promise<void>;
}

// ============================================
// CONTEXT
// ============================================

const SalesContext = createContext<SalesContextType | undefined>(undefined);

export const useSales = () => {
  const context = useContext(SalesContext);
  if (!context) {
    if (import.meta.env.DEV) {
      const defaultError = () => { throw new Error('SalesProvider not available'); };
      return {
        sales: [],
        loading: false,
        totalCount: 0,
        page: 0,
        pageSize: 50,
        setPage: () => {},
        setPageSize: () => {},
        listSearch: '',
        setListSearch: () => {},
        getSaleById: () => undefined,
        createSale: defaultError as SalesContextType['createSale'],
        updateSale: defaultError,
        deleteSale: defaultError,
        recordPayment: defaultError,
        updateShippingStatus: defaultError,
        convertQuotationToInvoice: defaultError,
        refreshSales: async () => {},
      } as SalesContextType;
    }
    throw new Error('useSales must be used within SalesProvider');
  }
  const activate = (context as SalesContextType & { __activate?: () => void }).__activate;
  useEffect(() => {
    activate?.();
  }, [activate]);
  return context;
};

// ============================================
// MOCK DATA (Initial)
// ============================================

const INITIAL_SALES: Sale[] = [
  {
    id: 'sale-1',
    invoiceNo: 'INV-0001',
    type: 'invoice',
    customer: 'Ahmed Retailers',
    customerName: 'Ahmed Ali',
    contactNumber: '+92-300-1234567',
    date: '2024-01-15',
    location: 'Main Branch (HQ)',
    items: [],
    itemsCount: 12,
    subtotal: 45000,
    discount: 0,
    tax: 0,
    expenses: 500,
    total: 45500,
    paid: 45500,
    due: 0,
    returnDue: 0,
    paymentStatus: 'paid',
    paymentMethod: 'Cash',
    shippingStatus: 'delivered',
    createdAt: '2024-01-15T10:30:00Z',
    updatedAt: '2024-01-15T10:30:00Z',
  },
  {
    id: 'sale-2',
    invoiceNo: 'INV-0002',
    type: 'invoice',
    customer: 'Walk-in Customer',
    customerName: 'Sara Khan',
    contactNumber: '+92-321-9876543',
    date: '2024-01-15',
    location: 'Mall Outlet',
    items: [],
    itemsCount: 3,
    subtotal: 8000,
    discount: 0,
    tax: 0,
    expenses: 200,
    total: 8200,
    paid: 5000,
    due: 3200,
    returnDue: 0,
    paymentStatus: 'partial',
    paymentMethod: 'Card',
    shippingStatus: 'pending',
    createdAt: '2024-01-15T11:00:00Z',
    updatedAt: '2024-01-15T11:00:00Z',
  },
  {
    id: 'sale-3',
    invoiceNo: 'QUO-0001',
    type: 'quotation',
    customer: 'Local Store',
    customerName: 'Bilal Ahmed',
    contactNumber: '+92-333-5555555',
    date: '2024-01-14',
    location: 'Main Branch (HQ)',
    items: [],
    itemsCount: 24,
    subtotal: 98000,
    discount: 0,
    tax: 0,
    expenses: 1200,
    total: 99200,
    paid: 0,
    due: 99200,
    returnDue: 0,
    paymentStatus: 'unpaid',
    paymentMethod: 'Credit',
    shippingStatus: 'pending',
    createdAt: '2024-01-14T14:20:00Z',
    updatedAt: '2024-01-14T14:20:00Z',
  },
];

// ============================================
// PROVIDER
// ============================================

// Helper function to normalize payment method to enum values
// Returns lowercase enum values: 'cash', 'bank', 'card', 'other'
const normalizePaymentMethodForEnum = (method: string | undefined): string => {
  if (!method) return 'cash';
  const normalized = method.toLowerCase().trim();
  const enumMap: Record<string, string> = {
    'cash': 'cash',
    'Cash': 'cash',
    'mobile wallet': 'card', // Mobile Wallet maps to 'card' enum in database
    'Mobile Wallet': 'card',
    'other': 'card', // Legacy 'other' maps to 'card' for backward compatibility
    'Other': 'card',
    'bank': 'bank',
    'Bank': 'bank',
    'card': 'card',
    'Card': 'card',
    'cheque': 'other',
    'Cheque': 'other',
    'wallet': 'other',
    'Wallet': 'other',
  };
  return enumMap[method] || enumMap[normalized] || 'cash';
};

// CRITICAL FIX: Export convertFromSupabaseSale as a standalone function
// This allows it to be used outside the context provider
export const convertFromSupabaseSale = (supabaseSale: any): Sale => {
  // Resolve branch display: use joined branch data if available
  // UI Rule: Show only branch NAME (not code, never UUID)
  let locationDisplay = '';
  if (supabaseSale.branch) {
    // Branch data joined from API - show NAME only
    locationDisplay = supabaseSale.branch.name || '';
  }
  // Note: Do NOT fallback to branch_id UUID - it should never appear in UI
  
    const displayNo = getSaleDisplayNumber(supabaseSale);
    return {
      id: supabaseSale.id,
      invoiceNo: displayNo || supabaseSale.invoice_no || '',
      draftNo: supabaseSale.draft_no ?? undefined,
      quotationNo: supabaseSale.quotation_no ?? undefined,
      orderNo: supabaseSale.order_no ?? undefined,
      type: supabaseSale.status === 'quotation' ? 'quotation' : 'invoice',
    status: supabaseSale.status || (supabaseSale.type === 'invoice' ? 'final' : 'quotation'),
      customer: supabaseSale.customer_id || '',
      customerName: supabaseSale.customer_name || '',
      contactNumber: supabaseSale.customer?.phone || '',
      date: supabaseSale.invoice_date || localNowDateString(),
    location: locationDisplay,
    branchId: supabaseSale.branch_id || supabaseSale.branch?.id || undefined,
    items: (supabaseSale.items || []).map((item: any) => {
      // Packing: single source of truth from backend (same as Purchase – parse if JSON string from API)
      const rawPd = item.packing_details;
      const pd = rawPd != null && typeof rawPd === 'string'
        ? (() => { try { return JSON.parse(rawPd); } catch { return null; } })()
        : rawPd;
      const packingDetails = pd && typeof pd === 'object'
        ? { ...pd, total_boxes: pd.total_boxes ?? 0, total_pieces: pd.total_pieces ?? 0, total_meters: pd.total_meters ?? item.packing_quantity ?? 0, boxes: pd.boxes || [] }
        : (item.packing_quantity != null && item.packing_quantity !== '')
          ? { total_boxes: 0, total_pieces: 0, total_meters: Number(item.packing_quantity) || 0, boxes: [] as any[] }
          : undefined;
      const customizationDetails = (() => {
        const raw = item.customization_details;
        if (raw == null) return undefined;
        if (typeof raw === 'string') {
          try { return parseCustomizationDetails(JSON.parse(raw)); } catch { return undefined; }
        }
        return parseCustomizationDetails(raw);
      })();
      const storedUnitPrice = Number(item.unit_price) || 0;
      const baseUnitPrice = deriveBaseUnitPriceFromStored(storedUnitPrice, customizationDetails);
      const effectiveUnitPrice = resolveSaleLineUnitPrice({
        price: storedUnitPrice,
        baseUnitPrice,
        customizationDetails,
      });
      return {
        id: item.id || '',
        productId: item.product_id || '',
        productName: item.product_name || '',
        sku: item.sku || item.product?.sku || 'N/A',
        quantity: item.quantity || 0,
        price: effectiveUnitPrice,
        baseUnitPrice,
        discount: item.discount_amount || 0,
        tax: item.tax_amount || 0,
        total: item.total || effectiveUnitPrice * (item.quantity || 0),
        variationId: item.variation_id || undefined,
        unit: item.unit || undefined,
        size: item.variation?.size || item.size || undefined,
        color: item.variation?.color || item.color || undefined,
        packingDetails: packingDetails ?? undefined,
        thaans: packingDetails?.total_boxes ?? item.packing_details?.thaans,
        meters: packingDetails?.total_meters ?? item.packing_quantity ?? undefined,
        customizationDetails,
        isStudioProduct: item.is_studio_product === true,
        bespokeParentItemId: item.bespoke_parent_item_id ?? null,
      };
    }),
      itemsCount: supabaseSale.items?.length || 0,
      subtotal: supabaseSale.subtotal || 0,
      discount: supabaseSale.discount_amount || 0,
      tax: supabaseSale.tax_amount || 0,
    expenses: extraExpenseAmountFromSaleRow(supabaseSale),
    shippingCharges: shippingChargeAmountFromSaleRow(supabaseSale),
    otherCharges: supabaseSale.other_charges || 0, // Extra charges if any
      total: supabaseSale.total || 0,
      studioCharges: supabaseSale.studio_charges != null ? Number(supabaseSale.studio_charges) : undefined,
      paid: supabaseSale.paid_amount || 0,
      due: supabaseSale.due_amount || 0,
    returnDue: supabaseSale.return_due || 0,
      paymentStatus: supabaseSale.payment_status || 'unpaid',
    paymentMethod: supabaseSale.payment_method || 'Cash',
    shippingStatus: (supabaseSale.shipment_status || supabaseSale.shipping_status || 'pending') as ShippingStatus | string,
      hasShipment: !!supabaseSale.first_shipment_id,
      firstShipmentId: supabaseSale.first_shipment_id || undefined,
      notes: supabaseSale.notes,
      customerBillRef: readSaleBillRef(supabaseSale, {
        isStudio: !!supabaseSale.is_studio || String(supabaseSale.order_no || '').startsWith('STD'),
      }) || undefined,
      deadline: supabaseSale.deadline || undefined,
      // CRITICAL FIX: Preserve attachments from database
      attachments: supabaseSale.attachments || null,
      // Line-level charges (sale_charges) for drawer/views to show actual data
      charges: Array.isArray(supabaseSale.charges) ? supabaseSale.charges : (Array.isArray(supabaseSale.sale_charges) ? supabaseSale.sale_charges : []),
      createdAt: supabaseSale.created_at || getCurrentLocalTimestamp(),
      updatedAt: supabaseSale.updated_at || getCurrentLocalTimestamp(),
    is_studio: !!supabaseSale.is_studio,
    createdBy: (supabaseSale.created_by?.full_name ?? supabaseSale.created_by_user?.full_name) || undefined,
    source: (supabaseSale as any).source ?? undefined,
    source_id: (supabaseSale as any).source_id ?? undefined,
    show_studio_breakdown: !!(supabaseSale as any).show_studio_breakdown,
    salesmanId: supabaseSale.salesman_id ?? undefined,
    commissionAmount: supabaseSale.commission_amount != null ? Number(supabaseSale.commission_amount) : undefined,
    commissionStatus: (supabaseSale as any).commission_status ?? undefined,
    commissionBatchId: (supabaseSale as any).commission_batch_id ?? undefined,
    commissionPercent: (supabaseSale as any).commission_percent != null ? Number((supabaseSale as any).commission_percent) : undefined,
  };
};

const DEFAULT_PAGE_SIZE = 50;
const ALLOWED_PAGE_SIZES = [50, 100, 200, 500] as const;
/** Hard cap when Rows = All (-1). */
const ALL_PAGE_SIZE_CAP = 2000;

export const SalesProvider = ({ children }: { children: ReactNode }) => {
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPageState] = useState(0);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [listSearch, setListSearchState] = useState('');
  const { generateDocumentNumber, incrementNextNumber, getNumberingConfig } = useDocumentNumbering();
  const accounting = useAccountingOptional();
  const { companyId, branchId, user, userRole } = useSupabase();
  const { modules, inventorySettings } = useSettings();
  const { formatCurrency } = useFormatCurrency();

  // Load sales from database (server-paginated)
  const loadSales = useCallback(async () => {
    if (!companyId) return;
    try {
      setLoading(true);
      const isAll = pageSize === -1;
      const effectiveLimit = isAll ? ALL_PAGE_SIZE_CAP : pageSize;
      const effectiveOffset = isAll ? 0 : page * pageSize;
      const result = await saleService.getAllSales(
        companyId,
        branchId === 'all' ? undefined : branchId || undefined,
        {
          offset: effectiveOffset,
          limit: effectiveLimit,
          search: listSearch || undefined,
        }
      );
      const isPaginated = result && typeof result === 'object' && 'data' in result && 'total' in result;
      const data = isPaginated ? (result as { data: any[]; total: number }).data : (result as any[]);
      const total = isPaginated ? (result as { data: any[]; total: number }).total : data.length;
      let mapped = data.map(convertFromSupabaseSale);
      const pageIds = new Set(mapped.map((s) => s.id));
      const missingRecent = recentCreatedSaleIdsRef.current.filter((id) => !pageIds.has(id));
      if (missingRecent.length > 0) {
        const extraRows = await Promise.all(
          missingRecent.map((id) => saleService.getSaleById(id).catch(() => null))
        );
        const extras = extraRows
          .filter(Boolean)
          .map((row) => convertFromSupabaseSale(row!));
        if (extras.length > 0) {
          mapped = [...extras, ...mapped];
        }
      }
      setSales(mapped);
      setTotalCount(total);
    } catch (error) {
      console.error('[SALES CONTEXT] Error loading sales:', error);
      toast.error('Failed to load sales');
      setSales([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId, page, pageSize, listSearch]);

  const setPage = useCallback((p: number) => {
    setPageState(Math.max(0, p));
  }, []);

  const setPageSize = useCallback((size: number) => {
    if (size === -1) {
      setPageSizeState(-1);
      setPageState(0);
      return;
    }
    const next = (ALLOWED_PAGE_SIZES as readonly number[]).includes(size)
      ? size
      : DEFAULT_PAGE_SIZE;
    setPageSizeState(next);
    setPageState(0);
  }, []);

  const setListSearch = useCallback((q: string) => {
    setListSearchState(String(q || '').trim());
    setPageState(0);
  }, []);

  const activatedRef = React.useRef(false);
  const [activated, setActivated] = React.useState(false);
  const recentCreatedSaleIdsRef = React.useRef<string[]>([]);
  const rememberRecentCreatedSale = useCallback((saleId: string) => {
    recentCreatedSaleIdsRef.current = [
      saleId,
      ...recentCreatedSaleIdsRef.current.filter((id) => id !== saleId),
    ].slice(0, 10);
  }, []);
  const activate = useCallback(() => {
    if (!activatedRef.current) { activatedRef.current = true; setActivated(true); }
  }, []);

  useEffect(() => {
    if (companyId && activated) loadSales();
    else if (!companyId) setLoading(false);
  }, [companyId, activated, loadSales, page]);

  const patchSaleInList = useCallback(async (saleId: string) => {
    if (!companyId) return;
    try {
      const row = await saleService.getSaleById(saleId);
      if (!row) return;
      const updated = convertFromSupabaseSale(row);
      setSales((prev) => {
        const idx = prev.findIndex((s) => s.id === saleId);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = updated;
          return next;
        }
        return [updated, ...prev];
      });
    } catch (e) {
      console.warn('[SALES CONTEXT] patchSaleInList failed:', e);
    }
  }, [companyId]);

  useEffect(() => {
    const onSaleUpdated = (ev: Event) => {
      if (!companyId) return;
      const detail = (ev as CustomEvent<{ saleId?: string; fullReload?: boolean }>).detail;
      if (detail?.fullReload) {
        void loadSales();
        return;
      }
      if (detail?.saleId) {
        void patchSaleInList(detail.saleId);
        return;
      }
      void loadSales();
    };
    window.addEventListener('saleUpdated', onSaleUpdated);
    return () => window.removeEventListener('saleUpdated', onSaleUpdated);
  }, [companyId, loadSales, patchSaleInList]);

  useEffect(() => {
    if (!companyId) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const queue = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        void loadSales();
      }, 220);
    };
    const onDataInvalidated = (ev: Event) => {
      const detail = (ev as CustomEvent<DataInvalidationDetail>).detail;
      const reason = String(detail?.reason ?? '');
      if (reason.includes('fallback-poll')) return;
      if (
        reason.includes('sales-context-create') &&
        detail?.entityId &&
        detail?.companyId === companyId
      ) {
        setPageState(0);
        void patchSaleInList(String(detail.entityId));
        return;
      }
      if (
        !shouldAcceptInvalidation(detail, {
          domain: ['sales', 'contacts', 'studio'],
          companyId,
          branchId: branchId === 'all' ? null : branchId ?? null,
        })
      ) {
        return;
      }
      if (
        (reason.includes('sales-context-payment') || reason.includes('sale-payment')) &&
        detail?.entityId
      ) {
        void patchSaleInList(String(detail.entityId));
        return;
      }
      queue();
    };
    window.addEventListener(DATA_INVALIDATED_EVENT, onDataInvalidated as EventListener);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(DATA_INVALIDATED_EVENT, onDataInvalidated as EventListener);
    };
  }, [branchId, companyId, loadSales, patchSaleInList]);

  useEffect(() => {
    setPageState(0);
  }, [companyId, branchId]);

  // When page changes we refetch from server; SalesPage filters/sorts the current page only

  // Get sale by ID (stable ref — avoids consumer useEffect loops when only unrelated context fields change)
  const getSaleById = useCallback((id: string): Sale | undefined => {
    return sales.find((s) => s.id === id);
  }, [sales]);

  // Create new sale
  const createSale = async (
    saleData: Omit<Sale, 'id' | 'invoiceNo' | 'createdAt' | 'updatedAt'>
  ): Promise<Sale> => {
    const perf = perfStart('createSale');
    if (!companyId || !user) {
      throw new Error('Company ID and User are required');
    }
    // DB requires branch_id UUID. Prefer sale header branch (SaleForm `location`) — context branch is often `'all'` for admins.
    let effectiveBranchId = branchIdFromSaleHeader((saleData as any).location, branchId);
    if (!effectiveBranchId) {
      const branches = await branchService.getAllBranches(companyId);
      if (!branches?.length) throw new Error('No branch found. Please create at least one branch.');
      effectiveBranchId = branches[0].id;
    }

    try {
      // CRITICAL FIX: Generate document number based on sale source + status (central numbering only)
      // Regular sale (final) → invoice → SL-0001. Studio → studio → STD-0001. POS → POS-. Draft/Quotation/Order → respective prefix.
      // SL and STD have separate counters; never mix.
      // Same-row lifecycle: stage numbers go in draft_no / quotation_no / order_no; invoice_no only when status is final.
      const isPOS = (saleData as any).isPOS === true;
      const isStudioSale = (saleData as any).isStudioSale === true;
      // Studio orders must have at least one product (fabric/material); block creation of empty studio sales
      if (isStudioSale && (!saleData.items || saleData.items.length === 0)) {
        throw new Error('Studio order must have at least one product (fabric/material). Add an item in the sale before saving.');
      }
      if (isStudioSale) {
        const dn = String((saleData as any).studioDesignName ?? '').trim();
        if (!dn) {
          throw new Error('Studio product name is required.');
        }
      }
      let docType: 'draft' | 'quotation' | 'order' | 'invoice' | 'pos' | 'studio' = 'invoice';
      if (isPOS) {
        docType = 'pos';
      } else if (isStudioSale) {
        docType = 'studio';
      } else if (saleData.status === 'draft') {
        docType = 'draft';
      } else if (saleData.status === 'quotation') {
        docType = 'quotation';
      } else if (saleData.status === 'order') {
        docType = 'order';
      } else if (saleData.type === 'invoice' || saleData.status === 'final') {
        docType = 'invoice';
      }
      // Global document number: SDR/SQT/SOR (non-posted), SL (final invoice), PS (POS), STD (studio)
      const sequenceType =
        docType === 'draft'
          ? 'SDR'
          : docType === 'quotation'
            ? 'SQT'
            : docType === 'order'
              ? 'SOR'
              : docType === 'studio'
                ? 'STD'
                : docType === 'pos'
                  ? 'PS'
                  : 'SL';

      const fromContext = inventorySettings.negativeStockAllowed === true;
      const preflight = perfStart('createSale.preflight');
      const [invoiceNoResult, allowNegativeResult, authUserResult] = await Promise.all([
        documentNumberService.getNextDocumentNumberGlobal(companyId, sequenceType),
        fromContext
          ? Promise.resolve(true)
          : import('@/app/services/settingsService').then((m) =>
              m.settingsService.getAllowNegativeStock(companyId),
            ),
        import('@/lib/supabase').then((m) => m.supabase.auth.getUser()),
      ]);
      preflight.end();

      let invoiceNo: string;
      try {
        invoiceNo = invoiceNoResult;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        throw new Error(
          `Could not allocate document number (${sequenceType}). Apply migrations on the server (get_next_document_number_global self-heal) and retry. ${msg}`
        );
      }
      const weGeneratedNumber = true;
      if (!invoiceNo || invoiceNo.includes('undefined') || invoiceNo.includes('NaN')) {
        throw new Error(`Invalid invoice number generated: ${invoiceNo}. Check document numbering settings.`);
      }

      const allowNegative = fromContext || allowNegativeResult === true;
      if (import.meta.env?.DEV) {
        console.log('[SALES CONTEXT] Negative stock:', { allowNegative, fromContext, fromDb: allowNegativeResult, inventorySettingsNegative: inventorySettings.negativeStockAllowed });
      }

      // Identity: created_by must be auth.uid() (never public.users.id)
      const authUser = authUserResult.data?.user;
      const createdByAuthId = authUser?.id ?? user?.auth_user_id ?? user?.id;

      // Convert to Supabase format (use effectiveBranchId – valid UUID for DB)
      const salesmanIdVal = (saleData as any).salesmanId && (saleData as any).salesmanId !== 'none' && (saleData as any).salesmanId !== '1' ? (saleData as any).salesmanId : null;
      const commissionAmountVal = Number((saleData as any).commissionAmount) || 0;
      const rowStatus =
        docType === 'draft'
          ? 'draft'
          : docType === 'quotation'
            ? 'quotation'
            : docType === 'order'
              ? 'order'
              : docType === 'invoice' || docType === 'pos'
                ? saleData.status ?? 'final'
                : docType === 'studio'
                  ? (saleData.status as any) ?? 'order'
                  : (saleData.status ?? (saleData.type === 'invoice' ? 'final' : 'quotation'));
      const isCreateFinal = canPostStockForSaleStatus(rowStatus);
      const draft_no = docType === 'draft' ? invoiceNo : undefined;
      const quotation_no = docType === 'quotation' ? invoiceNo : undefined;
      const order_no = docType === 'order' || docType === 'studio' ? invoiceNo : undefined;
      const invoice_no_forRow = docType === 'invoice' || docType === 'pos' ? invoiceNo : undefined;

      const supabaseSale: SupabaseSale = {
        company_id: companyId,
        branch_id: effectiveBranchId,
        invoice_no: invoice_no_forRow,
        draft_no,
        quotation_no,
        order_no,
        invoice_date: saleData.date,
        customer_id: saleData.customer || undefined,
        customer_name: saleData.customerName,
        type: saleData.type === 'invoice' ? 'invoice' : 'quotation',
        status: rowStatus,
        payment_status: saleData.paymentStatus,
        payment_method: saleData.paymentMethod ? normalizePaymentMethodForEnum(saleData.paymentMethod) : undefined,
        subtotal: saleData.subtotal,
        discount_amount: saleData.discount || 0,
        tax_amount: saleData.tax || 0,
        expenses: saleData.expenses || 0, // Database has 'expenses' not 'shipping_charges'
        extra_expenses: (() => {
          const list = (saleData as { extraExpenses?: SaleExtraExpenseInput[] }).extraExpenses;
          const charged = sumChargedExtraExpenses(list, isChargeExtrasOnCustomerBill(saleData as object));
          if (charged > 0) return charged;
          const ship = Number((saleData as { shippingCharges?: number }).shippingCharges ?? 0) || 0;
          return Math.max(0, (saleData.expenses || 0) - ship);
        })(),
        total: saleData.total,
        paid_amount: saleData.paid || 0,
        due_amount: saleData.due || 0,
        return_due: saleData.returnDue || 0,
        notes: saleData.notes,
        customer_bill_ref: (saleData as Sale).customerBillRef?.trim() || null,
        deadline: (saleData as any).deadline ?? null,
        created_by: createdByAuthId,
        salesman_id: salesmanIdVal,
        commission_amount: isCreateFinal ? commissionAmountVal : 0,
        commission_eligible_amount: isCreateFinal ? (saleData.subtotal ?? null) : null,
        commission_status: isCreateFinal && salesmanIdVal && commissionAmountVal > 0 ? 'pending' : null,
        commission_batch_id: null,
        commission_percent:
          isCreateFinal && (saleData as any).commissionPercent != null
            ? Number((saleData as any).commissionPercent)
            : null,
        // Do not send is_studio in insert – column may not exist yet; set via update after create
      };

      const supabaseItems: SupabaseSaleItem[] = saleData.items.map(item => {
        const customizationDetails = buildCustomizationDetailsForPersist((item as any).customizationDetails);
        const unitPrice = resolveSaleLineUnitPrice({
          price: item.price,
          unitPrice: (item as any).unitPrice,
          baseUnitPrice: (item as any).baseUnitPrice,
          customizationDetails: customizationDetails ?? undefined,
        });
        const qty = Number(item.quantity) || 0;
        const lineTotal = Number((item as any).total ?? unitPrice * qty) || 0;
        return {
        product_id: item.productId,
        variation_id: item.variationId || undefined,
        product_name: item.productName,
        sku: (item as any).sku || 'N/A', // Required in DB
        quantity: qty,
        unit: (item as any).unit && String((item as any).unit).trim() ? (item as any).unit : ((item as any).packingDetails?.unit || 'pcs'),
        unit_price: unitPrice, // POS sends unitPrice; SaleForm sends price – ensure never null for DB NOT NULL
        discount_amount: (item as any).discount || 0,
        tax_amount: (item as any).tax || 0,
        total: lineTotal,
        // Include packing data
        packing_type: (item as any).packingDetails?.packing_type || null,
        packing_quantity: (item as any).packingDetails?.total_meters || (item as any).meters || null,
        packing_unit: (item as any).packingDetails?.unit || 'meters',
        packing_details: (item as any).packingDetails || null,
        customization_details: customizationDetails,
        parent_line_index: (item as any).parentLineIndex ?? (item as any).parent_line_index,
        bespoke_parent_item_id: (item as any).bespokeParentItemId ?? (item as any).bespoke_parent_item_id,
      };
      });

      const allowNegativeStock = allowNegative; // DB value passed to service so it does not re-read
      let result: any;
      let effectiveInvoiceNo = invoiceNo;
      try {
        result = await saleService.createSale(supabaseSale, supabaseItems, {
          allowNegativeStock,
        });
        const deadlineErr = (result as any)?.deadlineError;
        if (deadlineErr) {
          toast.warning(`Deadline could not be saved: ${deadlineErr}. Run migrations/sales_set_deadline_rpc.sql in Supabase, then try again.`);
        } else if ((saleData as any).deadline && result && (result.deadline == null || result.deadline === '')) {
          toast.warning(
            'Notes saved. Deadline did not persist. Run "migrations/sales_set_deadline_rpc.sql" in Supabase SQL Editor, then save again.'
          );
        }
      } catch (insertError: any) {
        const msg = insertError?.message || '';
        // PostgREST returns HTTP 409 for unique violations; Postgres code 23505. Only retry for invoice number collisions (not other unique indexes).
        const isDuplicateInvoice =
          isSalesInvoiceNoUniqueViolation(insertError) ||
          (msg && String(msg).includes('sales_company_branch_invoice_unique'));
        if (isDuplicateInvoice) {
          // Retry with fresh number from DB. Studio uses order_no (STD), not invoice_no — setting invoice_no duplicated STD vs trigger and caused 409.
          try {
            effectiveInvoiceNo = await documentNumberService.getNextDocumentNumberGlobal(companyId, sequenceType);
            if (docType === 'studio') {
              supabaseSale.order_no = effectiveInvoiceNo;
              delete (supabaseSale as { invoice_no?: string | null }).invoice_no;
            } else if (docType === 'invoice' || docType === 'pos') {
              supabaseSale.invoice_no = effectiveInvoiceNo;
            } else if (docType === 'draft') {
              supabaseSale.draft_no = effectiveInvoiceNo;
              delete (supabaseSale as { invoice_no?: string | null }).invoice_no;
            } else if (docType === 'quotation') {
              supabaseSale.quotation_no = effectiveInvoiceNo;
              delete (supabaseSale as { invoice_no?: string | null }).invoice_no;
            } else if (docType === 'order') {
              supabaseSale.order_no = effectiveInvoiceNo;
              delete (supabaseSale as { invoice_no?: string | null }).invoice_no;
            }
            result = await saleService.createSale(supabaseSale, supabaseItems, {
              allowNegativeStock,
            });
            console.warn('[SALES CONTEXT] Duplicate document number; retried with DB number', docType, effectiveInvoiceNo);
          } catch (retryErr: any) {
            throw retryErr;
          }
        } else {
          throw insertError;
        }
      }
      // Line-level charges: extraExpenses + standalone shipping + discount (all persisted to sale_charges)
      const createExtraExpenses = (saleData as any).extraExpenses;
      const createShippingCharges = Number((saleData as any).shippingCharges ?? 0);
      if (result?.id) {
        const chargeOnBill = isChargeExtrasOnCustomerBill(saleData as object);
        const discountAmt = Number(saleData.discount ?? 0);
        const charges = await buildSaleChargeRowsFromExtras(
          companyId,
          createExtraExpenses as SaleExtraExpenseInput[] | undefined,
          chargeOnBill,
          createShippingCharges,
          discountAmt,
        );
        if (charges.length > 0) {
          await saleService.replaceSaleCharges(result.id, charges, createdByAuthId);
        }
      }
      // Set is_studio after create (avoids 400 if sales.is_studio column not yet added by migration).
      // Do NOT auto-create production, product, or invoice line here. Invoice is created only when user
      // clicks "Generate Invoice" (Create Product + Generate Invoice) in Studio Production.
      if (isStudioSale && result?.id) {
        try {
          const { supabase: sb } = await import('@/lib/supabase');
          await sb.from('sales').update({ is_studio: true }).eq('id', result.id);
        } catch (_) { /* column may not exist */ }
        const designTrim = String((saleData as any).studioDesignName ?? '').trim();
        if (designTrim && companyId && supabaseItems.length > 0) {
          try {
            const { supabase: sb } = await import('@/lib/supabase');
            const productionNo =
              String((result as any).order_no || (result as any).invoice_no || effectiveInvoiceNo || '').trim();
            const first = supabaseItems[0];
            const qtySum = supabaseItems.reduce((s, it) => s + (Number(it.quantity) || 0), 0);
            const quantity = qtySum > 0 ? qtySum : Number(first?.quantity) || 1;
            const { data: existingProd } = await sb
              .from('studio_productions')
              .select('id')
              .eq('sale_id', result.id)
              .maybeSingle();
            if (existingProd?.id) {
              await sb.from('studio_productions').update({ design_name: designTrim }).eq('id', existingProd.id);
            } else if (first?.product_id) {
              const productionDate = localNowDateString();
              await sb.from('studio_productions').insert({
                company_id: companyId,
                branch_id: effectiveBranchId,
                sale_id: result.id,
                production_no: productionNo || `STD-${result.id.slice(0, 8)}`,
                production_date: productionDate,
                product_id: first.product_id,
                variation_id: first.variation_id || null,
                quantity,
                status: 'draft',
                created_by: createdByAuthId ?? undefined,
                design_name: designTrim,
              });
            }
          } catch (studioProdErr) {
            console.warn('[SALES CONTEXT] Studio production design row failed (sale was created):', studioProdErr);
          }
        }
      }
      // Document number comes from DB (get_next_document_number_global); do not increment frontend counter
      
      // Convert back to app format (branch join missing on insert row — resolve name for list filters)
      let newSale = convertFromSupabaseSale(result);
      if ((!newSale.location || !String(newSale.location).trim()) && companyId && effectiveBranchId) {
        try {
          const branches = await branchService.getAllBranches(companyId);
          const bn = branches.find((b) => b.id === effectiveBranchId)?.name;
          if (bn) newSale = { ...newSale, location: bn };
        } catch {
          /* keep as-is */
        }
      }
      
      // Activity timeline: log sale creation (non-blocking)
      if (companyId && user?.id) {
        activityLogService.logActivity({
          companyId,
          module: 'sale',
          entityId: newSale.id,
          entityReference: newSale.invoiceNo,
          action: 'create',
          performedBy: user.id,
          description: `Sale ${newSale.invoiceNo} created`,
        }).catch((err) => console.warn('[SALES] Activity log create failed:', err));
      }
      
      // CRITICAL FIX: Record MULTIPLE payments if partialPayments array exists
      // Each payment method = separate payment record = separate reference number = separate journal entry
      const partialPayments = (saleData as any).partialPayments || [];
      
      // Payments table + payment JEs: only for posted (final) sales — draft paid fields are UI/draft only
      // saleService.recordPayment already posts JE via RPC + assert; do not also call accounting.recordSalePayment.
      if (
        newSale.paid > 0 &&
        companyId &&
        effectiveBranchId &&
        user &&
        canPostAccountingForSaleStatus(newSale.status)
      ) {
        try {
          const payPerf = perfStart('createSale.payments');
          const { accountHelperService } = await import('@/app/services/accountHelperService');
          const { saleService } = await import('@/app/services/saleService');
          const { accountService } = await import('@/app/services/accountService');

          const accountByMethod = new Map<string, string | null>();
          let cashFallbackId: string | null | undefined;
          const resolvePaymentAccountId = async (paymentMethod: string): Promise<string | null> => {
            if (accountByMethod.has(paymentMethod)) {
              return accountByMethod.get(paymentMethod) ?? null;
            }
            let paymentAccountId = await accountHelperService.getDefaultAccountByPaymentMethod(
              paymentMethod,
              companyId,
            );
            if (!paymentAccountId) {
              if (cashFallbackId === undefined) {
                const allAccounts = await accountService.getAllAccounts(companyId);
                cashFallbackId = allAccounts.find((acc) => acc.code === '1000')?.id || null;
              }
              paymentAccountId = cashFallbackId;
            }
            accountByMethod.set(paymentMethod, paymentAccountId);
            return paymentAccountId;
          };

          if (partialPayments.length > 0) {
            for (const partialPayment of partialPayments) {
              const paymentMethod = normalizePaymentMethodForEnum(partialPayment.method || 'cash');
              const paymentAccountId = await resolvePaymentAccountId(paymentMethod);
              if (paymentAccountId && partialPayment.amount > 0) {
                await saleService.recordPayment(
                  newSale.id,
                  partialPayment.amount,
                  paymentMethod,
                  paymentAccountId,
                  companyId,
                  effectiveBranchId,
                  saleData.date,
                  undefined,
                );
              }
            }
          } else {
            const paymentMethod = normalizePaymentMethodForEnum(saleData.paymentMethod || 'cash');
            let paymentAccountId: string | null = null;

            const { supabase } = await import('@/lib/supabase');
            const { data: existingPayment } = await supabase
              .from('payments')
              .select('id, payment_account_id')
              .eq('reference_type', 'sale')
              .eq('reference_id', newSale.id)
              .eq('amount', newSale.paid)
              .limit(1)
              .maybeSingle();

            if (existingPayment) {
              paymentAccountId = existingPayment.payment_account_id;
            } else {
              paymentAccountId = await resolvePaymentAccountId(paymentMethod);
              if (paymentAccountId) {
                await saleService.recordPayment(
                  newSale.id,
                  newSale.paid,
                  paymentMethod,
                  paymentAccountId,
                  companyId,
                  effectiveBranchId,
                  saleData.date,
                  undefined,
                );
              }
            }
          }
          payPerf.end();
        } catch (error: any) {
          console.error('[SALES CONTEXT] Error recording initial payment:', error);
          // Don't fail sale creation if payment recording fails
        }
      }
      
      setSales((prev) => [newSale, ...prev]);
      rememberRecentCreatedSale(newSale.id);

      if (
        branchId &&
        branchId !== 'all' &&
        newSale.branchId &&
        newSale.branchId !== branchId
      ) {
        const branchLabel = newSale.location?.trim() || 'another branch';
        toast.info(
          `Sale saved on ${branchLabel}. Switch header branch to All or ${branchLabel} to see it in the default list.`
        );
      }

      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('saleUpdated', { detail: { saleId: newSale.id, fullReload: false } })
        );
      }
      
      // Stock OUT: handled by DB trigger (if exists) + syncSaleStockForDocument (Z1 below).
      // The manual stock creation loop was removed because it raced with the DB trigger,
      // causing double entries. The Z1 sync at line ~1074 is idempotent and handles both cases:
      // - Trigger exists → sync sees correct movements, delta=0, skips
      // - Trigger missing → sync creates movements from sale_items
      if (canPostStockForSaleStatus(newSale.status) && newSale.items && newSale.items.length > 0) {
        // Skip manual stock creation — syncSaleStockForDocument handles it below.
        console.log('[SALES CONTEXT] Stock movements delegated to Z1 sync for sale:', newSale.id);
        if (false) {
          // DISABLED: Manual stock creation loop — kept for reference only
        console.log('[SALES CONTEXT] 🔄 Creating stock movements for sale:', newSale.id, 'Items:', newSale.items.length);
        
        const stockMovementErrors: string[] = [];

        // Fetch weighted average cost for all products from stock_movements (actual purchase/opening cost)
        // Falls back to product.cost_price if no purchase movements exist
        const saleProductIds = newSale.items.map((i: any) => i.productId).filter(Boolean);
        const costPriceMap = new Map<string, number>();
        if (saleProductIds.length > 0 && companyId) {
          const { data: movements } = await supabase
            .from('stock_movements')
            .select('product_id, quantity, unit_cost, total_cost')
            .eq('company_id', companyId)
            .in('product_id', saleProductIds)
            .in('movement_type', ['purchase', 'opening_stock']);
          // Calculate weighted average per product
          const costAcc = new Map<string, { sum: number; qty: number }>();
          for (const m of movements || []) {
            const pid = (m as any).product_id;
            const mQty = Math.abs(Number((m as any).quantity) || 0);
            const mCost = Math.abs(Number((m as any).total_cost) || (mQty * (Number((m as any).unit_cost) || 0)));
            const acc = costAcc.get(pid) || { sum: 0, qty: 0 };
            acc.sum += mCost;
            acc.qty += mQty;
            costAcc.set(pid, acc);
          }
          for (const [pid, acc] of costAcc) {
            if (acc.qty > 0) costPriceMap.set(pid, acc.sum / acc.qty);
          }
          // Fallback: products without purchase movements use cost_price
          const missingIds = saleProductIds.filter((id: string) => !costPriceMap.has(id));
          if (missingIds.length > 0) {
            const { data: fallback } = await supabase.from('products').select('id, cost_price').in('id', missingIds);
            for (const r of fallback || []) {
              if (!costPriceMap.has(r.id)) costPriceMap.set(r.id, Number((r as any).cost_price) || 0);
            }
          }
        }

        for (const item of newSale.items) {
          if (item.productId && item.quantity > 0) {
            try {
              // ============================================
              // COMBO HANDLING: Virtual Bundle Model
              // ============================================
              // If product is a combo, create stock movements for combo items
              // NOT for the combo product itself (combo product has no stock)
              // ============================================
              
              if (modules.combosEnabled) {
                const isCombo = await comboService.isComboProduct(item.productId, companyId);
                
                if (isCombo) {
                  console.log('[SALES CONTEXT] 🔄 Product is a combo, processing combo items:', {
                    combo_product_id: item.productId,
                    combo_quantity: item.quantity,
                    sale_id: newSale.id
                  });
                  
                  // Get combo details
                  const combo = await comboService.getComboByProductId(item.productId, companyId);
                  
                  if (!combo || !combo.items || combo.items.length === 0) {
                    throw new Error(`Combo product ${item.productId} has no items defined`);
                  }
                  
                  // Create stock movements for each combo item
                  for (const comboItem of combo.items) {
                    // Calculate quantity: combo_item.qty * sale_item.quantity
                    const componentQty = comboItem.qty * item.quantity;
                    
                    console.log('[SALES CONTEXT] Creating stock movement for combo item:', {
                      component_product_id: comboItem.product_id,
                      component_variation_id: comboItem.variation_id,
                      component_qty: componentQty,
                      combo_item_qty: comboItem.qty,
                      sale_item_qty: item.quantity
                    });
                    
                    // Create stock movement for component product (negative for stock OUT)
                    const movement = await productService.createStockMovement({
                      company_id: companyId,
                      branch_id: effectiveBranchId === 'all' ? undefined : effectiveBranchId,
                      product_id: comboItem.product_id,
                      variation_id: comboItem.variation_id || undefined,
                      movement_type: 'sale',
                      quantity: -componentQty, // Negative for stock OUT
                      unit_cost: costPriceMap.get(comboItem.product_id) || comboItem.unit_price || 0,
                      total_cost: -(componentQty * (costPriceMap.get(comboItem.product_id) || comboItem.unit_price || 0)),
                      reference_type: 'sale',
                      reference_id: newSale.id,
                      notes: `Combo sale ${newSale.invoiceNo}: ${combo.combo_name} - Component: ${comboItem.product_id}${comboItem.variation_id ? ' (Variation)' : ''}`,
                      created_by: createdByAuthId,
                    });
                    
                    if (!movement || !movement.id) {
                      throw new Error(`Stock movement creation returned null for combo component ${comboItem.product_id}`);
                    }
                    
                    console.log('[SALES CONTEXT] ✅ Stock movement created for combo component:', {
                      movement_id: movement.id,
                      product_id: comboItem.product_id,
                      variation_id: comboItem.variation_id,
                      quantity: movement.quantity
                    });
                    
                    // Stock is source-of-truth via stock_movements only. Do not update products.current_stock (column may not exist).
                  }
                  
                  // Skip creating stock movement for combo product itself (virtual bundle - no stock)
                  console.log('[SALES CONTEXT] ✅ Combo sale processed - stock movements created for all components');
                  continue;
                }
              }
              
              // ============================================
              // REGULAR PRODUCT HANDLING
              // ============================================
              console.log('[SALES CONTEXT] Creating stock movement for regular item:', {
                product_id: item.productId,
                variation_id: item.variationId,
                quantity: item.quantity,
                sale_id: newSale.id
              });
              
              const packing = (item as any).packingDetails;
              const boxOut = packing?.total_boxes != null ? Math.round(Number(packing.total_boxes)) : 0;
              const pieceOut = packing?.total_pieces != null ? Math.round(Number(packing.total_pieces)) : 0;
              const movement = await productService.createStockMovement({
                company_id: companyId,
                branch_id: effectiveBranchId === 'all' ? undefined : effectiveBranchId,
                product_id: item.productId,
                variation_id: item.variationId || undefined,
                movement_type: 'sale',
                quantity: -item.quantity,
                unit_cost: costPriceMap.get(item.productId) || item.price || 0,
                total_cost: -((costPriceMap.get(item.productId) || item.price || 0) * item.quantity),
                reference_type: 'sale',
                reference_id: newSale.id,
                notes: `Sale ${newSale.invoiceNo} - ${item.productName}${item.variationId ? ' (Variation)' : ''}`,
                created_by: createdByAuthId,
                box_change: -boxOut,
                piece_change: -pieceOut,
              });
              
              if (!movement || !movement.id) {
                throw new Error(`Stock movement creation returned null/undefined for product ${item.productId}`);
              }
              
              console.log('[SALES CONTEXT] ✅ Stock movement created:', {
                movement_id: movement.id,
                product_id: item.productId,
                variation_id: item.variationId,
                quantity: movement.quantity
              });
              
              // Stock is source-of-truth via stock_movements only. Do not update products.current_stock (column may not exist).
            } catch (movementError: any) {
              const errorMsg = `Failed to create stock movement for product ${item.productId} (${item.productName}): ${movementError.message || movementError}`;
              console.error('[SALES CONTEXT] ❌ Stock movement creation failed:', errorMsg);
              console.error('[SALES CONTEXT] Error details:', movementError);
              stockMovementErrors.push(errorMsg);
            }
          }
        }
        
        // CRITICAL: If any stock movement failed, throw error (no silent failures)
        if (stockMovementErrors.length > 0) {
          const errorMessage = `Stock movement creation failed for ${stockMovementErrors.length} item(s):\n${stockMovementErrors.join('\n')}`;
          console.error('[SALES CONTEXT] ❌ CRITICAL: Stock movements not created:', errorMessage);
          throw new Error(errorMessage);
        }
        
        console.log('[SALES CONTEXT] ✅ All stock movements created successfully for sale:', newSale.id);
        }
      }

      // Z1: Idempotent line ↔ movements reconciliation — background (mobile uses RPC inline; trigger + async reconcile is enough for UI responsiveness)
      if (newSale.id && (canPostStockForSaleStatus(newSale.status) || String(newSale.status).toLowerCase() === 'cancelled')) {
        void import('@/app/services/documentStockSyncService')
          .then(({ syncSaleStockForDocument }) => syncSaleStockForDocument(newSale.id))
          .then((z1) => {
            if (z1.adjustmentsInserted > 0) {
              console.log('[SALES CONTEXT] Z1 stock sync (create):', z1.keysAdjusted);
            }
          })
          .catch((z1Err) => console.warn('[SALES CONTEXT] Z1 stock sync (create) failed:', z1Err));
      }
      
      // Commission: NOT posted per sale (commission_status=pending until batch post).

      // 🔧 SALE DOCUMENT JE — single posting engine only (loads sale from DB; idempotent)
      if (canPostAccountingForSaleStatus(newSale.status) && companyId && newSale.total > 0) {
        const { postSaleDocumentAccounting } = await import('@/app/services/documentPostingEngine');
        const jeId = await postSaleDocumentAccounting(newSale.id);
        if (!jeId) {
          const { findActiveCanonicalSaleDocumentJournalEntryId } = await import('@/app/services/saleAccountingService');
          const existingDoc = await findActiveCanonicalSaleDocumentJournalEntryId(newSale.id);
          if (!existingDoc) {
            throw new Error(
              'Final sale was saved but no canonical document journal entry exists. Check posting engine / saleAccountingService logs (blocked non-final, missing accounts, or insert error).'
            );
          }
        }
      }
      
      // Payment JEs handled above via saleService.recordPayment (RPC + assert); no AccountingContext verify path.

      const createdLabel = docType === 'pos' ? 'POS sale' : docType === 'invoice' ? 'Invoice' : docType === 'quotation' ? 'Quotation' : docType === 'order' ? 'Order' : 'Draft';
      if (import.meta.env?.DEV) {
        console.info('[SALES CONTEXT] createSale complete:', createdLabel, effectiveInvoiceNo);
      }

      // Dispatch event to refresh inventory — defer heavy invalidation so UI can close spinner first
      window.dispatchEvent(new CustomEvent('saleSaved', { detail: { saleId: newSale.id } }));
      if (newSale.customer) {
        window.dispatchEvent(new CustomEvent('ledgerUpdated', { detail: { ledgerType: 'customer', entityId: newSale.customer } }));
      }

      if (companyId) {
        const invalidatePayload = {
          companyId,
          branchId: effectiveBranchId,
          customerId: newSale.customer || null,
          saleId: newSale.id,
          reason: 'sales-context-create' as const,
        };
        queueMicrotask(() => {
          setTimeout(() => dispatchSaleLifecycleInvalidated(invalidatePayload), 0);
        });
      }

      perf.end({ status: newSale.status, items: newSale.items?.length ?? 0 });
      return newSale;
    } catch (error: any) {
      console.error('[SALES CONTEXT] Error creating sale:', error);
      toast.error(`Failed to create sale: ${error.message || 'Unknown error'}`);
      throw error;
    }
  };

  // Update sale
  const updateSale = async (id: string, updates: Partial<Sale>): Promise<void> => {
    const traceId = createAccountingEditTraceId(id);
    try {
      // CRITICAL FIX: Handle full sale update (not just status)
      const supabaseUpdates: any = {};
      
      // Studio → STD (final invoice number for studio when applicable)
      if ((updates as any).is_studio === true && typeof (updates as any).invoiceNo === 'string' && (updates as any).invoiceNo) {
        supabaseUpdates.invoice_no = (updates as any).invoiceNo;
      } else if (
        (updates.status === 'final' || updates.type === 'invoice') &&
        typeof (updates as any).invoiceNo === 'string' &&
        String((updates as any).invoiceNo).trim() !== ''
      ) {
        const invNo = String((updates as any).invoiceNo).trim();
        // Pre-final numbers (SOR/SO/SDR/SQT) must not be written as invoice_no — SL allocator runs on finalize
        if (!isPreFinalSaleDocumentNo(invNo)) {
          supabaseUpdates.invoice_no = invNo;
        }
      }
      if ((updates as any).draftNo !== undefined) supabaseUpdates.draft_no = (updates as any).draftNo;
      if ((updates as any).quotationNo !== undefined) supabaseUpdates.quotation_no = (updates as any).quotationNo;
      if ((updates as any).orderNo !== undefined) supabaseUpdates.order_no = (updates as any).orderNo;

      if (updates.status !== undefined) supabaseUpdates.status = updates.status === 'invoice' ? 'final' : (updates.status as string);
      if (updates.type !== undefined) supabaseUpdates.type = updates.type === 'invoice' ? 'invoice' : 'quotation';
      if (updates.paymentStatus !== undefined) supabaseUpdates.payment_status = updates.paymentStatus;
      if (updates.total !== undefined) supabaseUpdates.total = updates.total;
      if (updates.subtotal !== undefined) supabaseUpdates.subtotal = updates.subtotal;
      if (updates.discount !== undefined) supabaseUpdates.discount_amount = updates.discount;
      if (updates.tax !== undefined) supabaseUpdates.tax_amount = updates.tax;
      if (updates.expenses !== undefined) {
        supabaseUpdates.expenses = updates.expenses;
        supabaseUpdates.extra_expenses = updates.expenses;
      }
      const extraExpensesForColumn = (updates as { extraExpenses?: SaleExtraExpenseInput[] }).extraExpenses;
      if (Array.isArray(extraExpensesForColumn)) {
        const extraSum = sumChargedExtraExpenses(
          extraExpensesForColumn,
          isChargeExtrasOnCustomerBill(updates as object),
        );
        supabaseUpdates.extra_expenses = extraSum;
        if (updates.expenses === undefined) {
          supabaseUpdates.expenses = extraSum;
        }
      }
      // CRITICAL FIX: DO NOT directly update paid_amount in sale
      // paid_amount should ONLY be updated by database trigger from payments table
      // If updates.paid is provided, we'll handle it by creating/updating payment record below
      // if (updates.paid !== undefined) supabaseUpdates.paid_amount = updates.paid; // REMOVED - Use payments table only
      if (updates.due !== undefined) supabaseUpdates.due_amount = updates.due;
      if (updates.date !== undefined) supabaseUpdates.invoice_date = updates.date;
      if (updates.customerName !== undefined) supabaseUpdates.customer_name = updates.customerName;
      // UUID column: empty string is invalid; use null for walk-in / no customer
      if (updates.customer !== undefined) {
        supabaseUpdates.customer_id = coerceUuidOrNull(updates.customer);
      }
      if (updates.location !== undefined) {
        supabaseUpdates.branch_id = coerceUuidOrNull(updates.location);
      }
      if (updates.notes !== undefined) supabaseUpdates.notes = updates.notes;
      if ((updates as any).customerBillRef !== undefined) {
        const refTrim = String((updates as any).customerBillRef ?? '').trim();
        supabaseUpdates.customer_bill_ref = refTrim || null;
      }
      if ((updates as any).deadline !== undefined) supabaseUpdates.deadline = (updates as any).deadline;
      if (updates.shippingStatus !== undefined) supabaseUpdates.shipping_status = updates.shippingStatus;
      if (updates.paymentMethod !== undefined) {
        supabaseUpdates.payment_method = normalizePaymentMethodForEnum(updates.paymentMethod);
      }
      if (updates.is_studio !== undefined) {
        supabaseUpdates.is_studio = updates.is_studio;
      }
      if ((updates as any).attachments !== undefined) supabaseUpdates.attachments = (updates as any).attachments;
      if ((updates as any).salesmanId !== undefined) {
        supabaseUpdates.salesman_id = coerceUuidOrNull((updates as any).salesmanId);
      }
      if ((updates as any).commissionAmount !== undefined) supabaseUpdates.commission_amount = Number((updates as any).commissionAmount) || 0;
      if ((updates as any).commissionEligibleAmount !== undefined) supabaseUpdates.commission_eligible_amount = (updates as any).commissionEligibleAmount;
      if ((updates as any).commissionPercent !== undefined) supabaseUpdates.commission_percent = (updates as any).commissionPercent != null ? Number((updates as any).commissionPercent) : null;

      // 🔒 CRITICAL FIX: Calculate stock movement DELTA BEFORE updating sale_items
      // This must happen BEFORE sale_items are deleted/updated so we can fetch old items
      const sale = getSaleById(id);
      pushAccountingEditTrace({
        traceId,
        ts: getCurrentLocalTimestamp(),
        module: 'sales',
        entityType: 'sale',
        entityId: id,
        companyId: companyId || null,
        branchId: branchId || null,
        phase: 'start',
        data: { updates },
      });
      if (sale) {
        const salesClassification = classifySalesEdit({
          oldSnap: {
            notes: sale.notes || '',
            referenceNo: sale.invoiceNo || '',
            date: sale.date || '',
            customerId: sale.customerId || '',
            itemQtyTotal: (sale.items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0),
            itemRateTotal: (sale.items || []).reduce((s, it) => s + (Number(it.price) || 0), 0),
            discount: Number(sale.discount || 0),
            shipping: Number((sale as any).shippingCharges || 0),
            paymentAccountId: (sale as any).paymentAccountId || '',
            branchId: sale.location || '',
            paymentStatus: sale.paymentStatus || '',
            saleType: sale.type || '',
          },
          newSnap: {
            notes: updates.notes ?? sale.notes ?? '',
            referenceNo: (updates as any).referenceNo ?? sale.invoiceNo ?? '',
            date: updates.date ?? sale.date ?? '',
            customerId: updates.customer ?? sale.customerId ?? '',
            itemQtyTotal: Array.isArray((updates as any).items)
              ? (updates as any).items.reduce((s: number, it: any) => s + (Number(it.quantity) || 0), 0)
              : (sale.items || []).reduce((s, it) => s + (Number(it.quantity) || 0), 0),
            itemRateTotal: Array.isArray((updates as any).items)
              ? (updates as any).items.reduce((s: number, it: any) => s + (Number(it.price || it.unitPrice) || 0), 0)
              : (sale.items || []).reduce((s, it) => s + (Number(it.price) || 0), 0),
            discount: updates.discount ?? sale.discount ?? 0,
            shipping: (updates as any).shippingCharges ?? (sale as any).shippingCharges ?? 0,
            paymentAccountId: (updates as any).paymentAccountId ?? (sale as any).paymentAccountId ?? '',
            branchId: updates.location ?? sale.location ?? '',
            paymentStatus: updates.paymentStatus ?? sale.paymentStatus ?? '',
            saleType: updates.type ?? sale.type ?? '',
          },
        });
        assertDomainEditSafetyTestMode(salesClassification, 'sales updateSale');
        pushAccountingEditTrace({
          traceId,
          ts: getCurrentLocalTimestamp(),
          module: 'sales',
          entityType: 'sale',
          entityId: id,
          companyId: companyId || null,
          branchId: branchId || null,
          phase: 'classified',
          data: salesClassification,
        });
      }
      const newStatusForGate =
        updates.status !== undefined
          ? (updates.status === 'invoice' ? 'final' : String(updates.status))
          : undefined;
      const priorPostedStock = sale != null && canPostStockForSaleStatus(sale.status);
      const willBePostedStock =
        newStatusForGate != null && canPostStockForSaleStatus(newStatusForGate);
      /** Stock deltas: only if already posted or this save moves to posted (finalize + item change). */
      const allowStockItemDeltas = priorPostedStock || willBePostedStock;
      // PF-14: Component-level GL only when sale was already posted before this edit (not draft→final in same payload — handled by finalize paths / service).
      const priorPostedAccounting = sale != null && canPostAccountingForSaleStatus(sale.status);
      const accountingRepostNeeded = priorPostedAccounting && (
        updates.total !== undefined ||
        updates.subtotal !== undefined ||
        updates.discount !== undefined ||
        (updates as any).shippingCharges !== undefined ||
        (updates as any).extraExpenses !== undefined ||
        ((updates as any).items && Array.isArray((updates as any).items))
      );
      let oldAccountingSnapshot: { total: number; subtotal: number; discount: number; extraExpense: number; shippingCharges: number } | null = null;
      let preUpdateSaleRow: Awaited<ReturnType<typeof saleService.getSaleById>> | null | undefined;
      const ensurePreUpdateSaleRow = async () => {
        if (preUpdateSaleRow !== undefined) return preUpdateSaleRow;
        preUpdateSaleRow = await saleService.getSaleById(id);
        return preUpdateSaleRow;
      };
      if (accountingRepostNeeded && companyId) {
        try {
          const oldSaleForAccounting = await ensurePreUpdateSaleRow();
          const { saleAccountingService: sac } = await import('@/app/services/saleAccountingService');
          oldAccountingSnapshot = sac.getSaleAccountingSnapshot(oldSaleForAccounting);
        } catch (e) {
          console.warn('[SALES CONTEXT] PF-14: Could not capture old accounting snapshot:', e);
        }
      }
      let stockMovementDeltas: Array<{
        productId: string;
        variationId?: string;
        deltaQty: number;
        price: number;
        name: string;
      }> = [];
      
      // Only calculate delta if items are being updated and sale is / will be posted for stock
      if (allowStockItemDeltas && (updates as any).items && Array.isArray((updates as any).items) && companyId) {
        try {
          console.log('[SALES CONTEXT] 🔄 Calculating stock movement DELTA for sale edit (BEFORE items update):', id);
          
          // STEP 1: Fetch OLD items from database (BEFORE they are deleted)
          const { supabase } = await import('@/lib/supabase');
          const { data: oldItemsData, error: oldItemsError } = await supabase
            .from('sales_items')
            .select('product_id, variation_id, quantity, unit_price, product_name')
            .eq('sale_id', id);
          
          if (oldItemsError) {
            console.error('[SALES CONTEXT] Error fetching old items:', oldItemsError);
            // Don't throw - continue without delta calculation
          } else {
            const oldItems = oldItemsData || [];
            const newItems = (updates as any).items || [];
            
            console.log('[SALES CONTEXT] Stock movement delta calculation:', {
              oldItemsCount: oldItems.length,
              newItemsCount: newItems.length,
              oldItems: oldItems.map((i: any) => ({ product_id: i.product_id, variation_id: i.variation_id, qty: i.quantity })),
              newItems: newItems.map((i: any) => ({ product_id: i.productId, variation_id: i.variationId, qty: i.quantity }))
            });
            
            // STEP 2: Build maps for delta calculation
            // Key format: "productId_variationId" (use "null" for no variation)
            type ItemKey = string;
            const oldItemsMap = new Map<ItemKey, { quantity: number; price: number; name: string }>();
            const newItemsMap = new Map<ItemKey, { quantity: number; price: number; name: string }>();
            
            // Map old items
            oldItems.forEach((item: any) => {
              const key: ItemKey = `${item.product_id}_${item.variation_id || 'null'}`;
              oldItemsMap.set(key, {
                quantity: Number(item.quantity) || 0,
                price: Number(item.unit_price) || 0,
                name: item.product_name || 'Unknown'
              });
            });
            
            // Map new items
            newItems.forEach((item: any) => {
              if (item.productId && item.quantity > 0) {
                const key: ItemKey = `${item.productId}_${item.variationId || 'null'}`;
                newItemsMap.set(key, {
                  quantity: Number(item.quantity) || 0,
                  price: Number(item.price) || 0,
                  name: item.productName || 'Unknown'
                });
              }
            });
            
            // STEP 3: Calculate DELTA for each product+variation combination
            // Check all keys (old + new)
            const allKeys = new Set([...oldItemsMap.keys(), ...newItemsMap.keys()]);
            
            allKeys.forEach((key) => {
              const oldItem = oldItemsMap.get(key) || { quantity: 0, price: 0, name: '' };
              const newItem = newItemsMap.get(key) || { quantity: 0, price: 0, name: '' };
              
              const deltaQty = newItem.quantity - oldItem.quantity;
              
              // Only create movement if delta is non-zero
              if (deltaQty !== 0) {
                const [productId, variationIdStr] = key.split('_');
                const variationId = variationIdStr === 'null' ? undefined : variationIdStr;
                
                stockMovementDeltas.push({
                  productId,
                  variationId,
                  deltaQty, // Positive = stock increased (item removed/reduced), Negative = stock decreased (item added/increased)
                  price: newItem.price || oldItem.price || 0,
                  name: newItem.name || oldItem.name || 'Unknown'
                });
                
                console.log('[SALES CONTEXT] Delta calculated:', {
                  key,
                  productId,
                  variationId,
                  oldQty: oldItem.quantity,
                  newQty: newItem.quantity,
                  deltaQty,
                  meaning: deltaQty > 0 ? 'Item removed/reduced (stock restored)' : 'Item added/increased (stock reduced)'
                });
              }
            });
          }
        } catch (deltaError: any) {
          console.error('[SALES CONTEXT] ❌ Delta calculation error:', deltaError);
          // Don't block sale update if delta calculation fails
        }
      }

      // Negative stock enforcement: company-level setting from DB only (same for all users)
      const reducingDeltas = stockMovementDeltas.filter((d) => d.deltaQty < 0);
      const allowNegativeUpdate =
        inventorySettings.negativeStockAllowed === true ||
        (companyId
          ? await import('@/app/services/settingsService').then((m) =>
              m.settingsService.getAllowNegativeStock(companyId)
            )
          : false);
      let headerBranchForStockEdit: string | undefined =
        updates.location !== undefined ? updates.location : sale?.location;
      if (headerBranchForStockEdit === undefined && companyId) {
        const row = await ensurePreUpdateSaleRow();
        headerBranchForStockEdit = (row as any)?.branch_id ?? undefined;
      }
      let effectiveBranchIdForStockEdit = branchIdFromSaleHeader(headerBranchForStockEdit, branchId);
      if (!effectiveBranchIdForStockEdit && companyId) {
        const branches = await branchService.getAllBranches(companyId);
        effectiveBranchIdForStockEdit = branches?.length ? branches[0].id : null;
      }
      if (!allowNegativeUpdate && reducingDeltas.length > 0 && companyId) {
        const { supabase } = await import('@/lib/supabase');
        const productIds = [...new Set(reducingDeltas.map((d) => d.productId))];
        const [stockMap, { data: prods }] = await Promise.all([
          productService.getStockForProducts(productIds, companyId, effectiveBranchIdForStockEdit ?? undefined),
          supabase.from('products').select('id, has_variations').in('id', productIds),
        ]);
        const productMap = new Map((prods || []).map((p: any) => [p.id, p]));

        for (const delta of reducingDeltas) {
          const p = productMap.get(delta.productId) as { has_variations?: boolean } | undefined;
          if (!p) continue;
          const key = delta.variationId ? `${delta.productId}:${delta.variationId}` : `${delta.productId}:`;
          const stock = stockMap.get(key) ?? 0;
          const qtyNeeded = Math.abs(delta.deltaQty);
          if (stock < qtyNeeded) {
            throw new Error(
              `${delta.name}: quantity (${qtyNeeded}) exceeds available stock (${stock}). ` +
              'To allow this, enable "Negative Stock Allowed" in Settings → Inventory.'
            );
          }
        }
      }

      // CRITICAL FIX: Update sale items if provided
      if ((updates as any).items && Array.isArray((updates as any).items)) {
        const { saleService } = await import('@/app/services/saleService');
        console.log('[SALES CONTEXT] 🔄 Updating sale items. Received items count:', (updates as any).items.length);
        console.log('[SALES CONTEXT] Items payload:', (updates as any).items.map((item: any, idx: number) => ({
          index: idx,
          id: item.id,
          productId: item.productId,
          productName: item.productName,
          quantity: item.quantity,
          price: item.price || item.unitPrice
        })));
        
        const { supabase } = await import('@/lib/supabase');
        const { data: existingItemRows } = await supabase
          .from('sales_items')
          .select('product_id, variation_id, customization_details')
          .eq('sale_id', id);
        const existingCustomByKey = new Map<string, Record<string, unknown>>();
        const existingRows = existingItemRows ?? [];
        for (const row of existingRows) {
          const pid = String((row as { product_id?: string }).product_id ?? '');
          const vid = String((row as { variation_id?: string }).variation_id ?? '');
          const parsed = parseCustomizationDetails((row as { customization_details?: unknown }).customization_details);
          if (pid && parsed && hasBespokeContent(parsed)) {
            const payload = parsed as Record<string, unknown>;
            existingCustomByKey.set(`${pid}:${vid}`, payload);
            if (vid) {
              existingCustomByKey.set(`${pid}:`, payload);
              existingCustomByKey.set(`${pid}:null`, payload);
            }
          }
        }

        const payloadItems = (updates as any).items as any[];
        const saleItems = payloadItems.map((item: any, index: number) => {
          const rawCustomization =
            item.customizationDetails ?? item.customization_details;
          let customizationDetails = buildCustomizationDetailsForPersist(rawCustomization);
          if (!customizationDetails) {
            const productId = String(item.productId ?? '');
            const variationId = item.variationId != null ? String(item.variationId) : '';
            const preserveKeys = [
              `${productId}:${variationId}`,
              `${productId}:`,
              `${productId}:null`,
            ];
            let preserved: Record<string, unknown> | undefined;
            for (const key of preserveKeys) {
              preserved = existingCustomByKey.get(key);
              if (preserved) break;
            }
            if (!preserved && payloadItems.length === 1 && existingRows.length === 1) {
              const parsed = parseCustomizationDetails(
                (existingRows[0] as { customization_details?: unknown }).customization_details,
              );
              if (parsed && hasBespokeContent(parsed)) {
                preserved = parsed as Record<string, unknown>;
              }
            }
            if (preserved) {
              customizationDetails = buildCustomizationDetailsForPersist(preserved) ?? (preserved as any);
            }
          }
          const unitPrice = resolveSaleLineUnitPrice({
            price: item.price,
            unitPrice: item.unitPrice,
            baseUnitPrice: item.baseUnitPrice,
            customizationDetails: customizationDetails ?? undefined,
          });
          const qty = Number(item.quantity) || 0;
          const lineTotal = Number(item.total ?? unitPrice * qty) || 0;
          const saleItem = {
          product_id: item.productId,
          variation_id: normalizeVariationIdForPersist(
            item.variationId,
            item.product as { has_variations?: boolean; variations?: unknown[] } | undefined,
          ),
          product_name: item.productName,
          sku: item.sku || 'N/A',
          quantity: qty,
          unit: item.unit || 'pcs',
          unit_price: unitPrice,
          discount_amount: item.discount || 0,
          tax_amount: item.tax || 0,
          total: lineTotal,
          packing_type: item.packingDetails?.packing_type || null,
          packing_quantity: item.packingDetails?.total_meters || item.meters || null,
          packing_unit: item.packingDetails?.unit || 'meters',
          packing_details: item.packingDetails || null,
          customization_details: customizationDetails,
          parent_line_index: (item as { parentLineIndex?: number }).parentLineIndex
            ?? (item as { parent_line_index?: number }).parent_line_index,
          bespoke_parent_item_id:
            (item as { bespokeParentItemId?: string }).bespokeParentItemId
            ?? (item as { bespoke_parent_item_id?: string }).bespoke_parent_item_id,
        };
        console.log(`[SALES CONTEXT] ✅ Converted item ${index}:`, {
          product_id: saleItem.product_id,
          product_name: saleItem.product_name,
          quantity: saleItem.quantity,
          unit_price: saleItem.unit_price,
          has_customization: customizationDetails != null,
        });
        return saleItem;
        });
        
        console.log('[SALES CONTEXT] ✅ Final saleItems array length:', saleItems.length);
        
        // Snapshot bespoke WO anchors so parent_sales_item_id can be restored after line replace
        try {
          await snapshotBespokeWorkOrderAnchors(id);
        } catch (snapshotErr) {
          const msg =
            snapshotErr instanceof Error
              ? snapshotErr.message
              : 'Cannot update sale lines while bespoke work orders are linked — contact support';
          toast.error(msg);
          throw snapshotErr;
        }

        // Delete existing items and insert new ones (WO FK is ON DELETE SET NULL after migration)
        const { error: deleteError } = await supabase.from('sales_items').delete().eq('sale_id', id);
        if (deleteError) {
          console.error('[SALES CONTEXT] ❌ Error deleting old items:', deleteError);
          throw deleteError;
        }
        console.log('[SALES CONTEXT] ✅ Deleted old sale_items');
        
        if (saleItems.length > 0) {
          console.log('[SALES CONTEXT] 🔄 Inserting', saleItems.length, 'new sale_items (ordered)');
          await insertSaleItemsOrdered(id, saleItems as import('@/app/services/saleService').SaleItem[]);
          console.log('[SALES CONTEXT] ✅ Successfully inserted', saleItems.length, 'sale_items');
        } else {
          console.log('[SALES CONTEXT] ⚠️ No items to insert (saleItems.length = 0)');
        }

        try {
          await relinkBespokeWorkOrdersAfterSaleItemReplace(id);
        } catch (relinkErr) {
          const msg =
            relinkErr instanceof Error
              ? relinkErr.message
              : 'Sale lines updated but bespoke work order link failed — contact support';
          toast.error(msg);
          throw relinkErr;
        }
      }

      // Persist header (status, invoice_no, totals) before stock + before saleService posts canonical JE on first final
      if (Object.keys(supabaseUpdates).length > 0) {
        await saleService.updateSale(id, supabaseUpdates);
      }

      // 🔒 CRITICAL FIX: Create stock movements for DELTA (after sale_items are updated)
      // GOLDEN RULE: Only create movements for the delta, not for all items
      if (stockMovementDeltas.length > 0 && companyId) {
        try {
          const { supabase: sb } = await import('@/lib/supabase');
          const { data: { user: authUser } } = await sb.auth.getUser();
          const updateCreatedByAuthId = authUser?.id ?? (user as any)?.auth_user_id ?? user?.id;

          console.log('[SALES CONTEXT] 🔄 Creating stock movements for deltas:', stockMovementDeltas.length);
          
          const saleForStock = await saleService.getSaleById(id);
          const saleBranchFromRow = (saleForStock as any)?.branch_id as string | undefined;
          let effectiveBranchId = branchIdFromSaleHeader(
            updates.location !== undefined ? updates.location : saleBranchFromRow,
            branchId
          );
          if (!effectiveBranchId && companyId) {
            const branches = await branchService.getAllBranches(companyId);
            effectiveBranchId = branches?.length ? branches[0].id : null;
          }
          
          // Create stock movements for DELTA only
          const stockMovementErrors: string[] = [];
          const saleInvoiceNo = getSaleDisplayNumber(saleForStock as any) || (saleForStock as any)?.invoice_no || id;

          // Fetch weighted average cost from stock_movements (actual purchase cost, not static cost_price)
          const editProductIds = stockMovementDeltas.map(d => d.productId).filter(Boolean);
          const editCostMap = new Map<string, number>();
          if (editProductIds.length > 0 && companyId) {
            const { supabase: sbDelta } = await import('@/lib/supabase');
            const { data: movements } = await sbDelta
              .from('stock_movements')
              .select('product_id, quantity, unit_cost, total_cost')
              .eq('company_id', companyId)
              .in('product_id', editProductIds)
              .in('movement_type', ['purchase', 'opening_stock']);
            const costAcc = new Map<string, { sum: number; qty: number }>();
            for (const m of movements || []) {
              const pid = (m as any).product_id;
              const mQty = Math.abs(Number((m as any).quantity) || 0);
              const mCost = Math.abs(Number((m as any).total_cost) || (mQty * (Number((m as any).unit_cost) || 0)));
              const acc = costAcc.get(pid) || { sum: 0, qty: 0 };
              acc.sum += mCost;
              acc.qty += mQty;
              costAcc.set(pid, acc);
            }
            for (const [pid, acc] of costAcc) {
              if (acc.qty > 0) editCostMap.set(pid, acc.sum / acc.qty);
            }
            const missingIds = editProductIds.filter((id: string) => !editCostMap.has(id));
            if (missingIds.length > 0) {
              const { data: fallback } = await sbDelta.from('products').select('id, cost_price').in('id', missingIds);
              for (const r of fallback || []) {
                if (!editCostMap.has(r.id)) editCostMap.set(r.id, Number((r as any).cost_price) || 0);
              }
            }
          }

          // Fetch existing edit movements for this sale to consolidate (avoid duplicate entries)
          const { supabase: sbEditMov } = await import('@/lib/supabase');
          const { data: existingEditMovements } = await sbEditMov
            .from('stock_movements')
            .select('id, product_id, variation_id, quantity, unit_cost, total_cost, notes')
            .eq('reference_type', 'sale')
            .eq('reference_id', id)
            .eq('movement_type', 'sale')
            .like('notes', '%Sale Edit%');
          const existingEditMap = new Map<string, any>();
          for (const em of existingEditMovements || []) {
            const key = `${(em as any).product_id}_${(em as any).variation_id || 'null'}`;
            existingEditMap.set(key, em);
          }

          for (const delta of stockMovementDeltas) {
            try {
              // deltaQty > 0 means item was removed/reduced → restore stock (positive movement)
              // deltaQty < 0 means item was added/increased → reduce stock (negative movement)
              const movementQty = -delta.deltaQty;
              const editKey = `${delta.productId}_${delta.variationId || 'null'}`;
              const existing = existingEditMap.get(editKey);

              if (existing) {
                // Consolidate: accumulate delta into existing edit movement
                const newQty = (Number(existing.quantity) || 0) + movementQty;
                const unitCost = editCostMap.get(delta.productId) || delta.price;
                if (Math.abs(newQty) < 0.001) {
                  // Net zero — remove the edit movement entirely
                  await sbEditMov.from('stock_movements').delete().eq('id', existing.id);
                  console.log('[SALES CONTEXT] ✅ Stock edit movement net-zero, removed:', delta.name);
                } else {
                  await sbEditMov.from('stock_movements').update({
                    quantity: newQty,
                    unit_cost: unitCost,
                    total_cost: Math.abs(newQty) * unitCost,
                    notes: `Sale Edit ${saleInvoiceNo} - ${delta.name}${delta.variationId ? ' (Variation)' : ''} (accumulated)`,
                  }).eq('id', existing.id);
                  console.log('[SALES CONTEXT] ✅ Stock edit movement consolidated:', delta.name, 'New qty:', newQty);
                }
              } else {
                // No existing edit movement — create new one
                console.log('[SALES CONTEXT] Creating stock movement for delta:', {
                  product_id: delta.productId,
                  variation_id: delta.variationId,
                  deltaQty: delta.deltaQty,
                  movementQty: movementQty,
                  sale_id: id
                });

                const movement = await productService.createStockMovement({
                  company_id: companyId,
                  branch_id: effectiveBranchId === 'all' ? undefined : effectiveBranchId,
                  product_id: delta.productId,
                  variation_id: delta.variationId,
                  movement_type: 'sale',
                  quantity: movementQty,
                  unit_cost: editCostMap.get(delta.productId) || delta.price,
                  total_cost: movementQty * (editCostMap.get(delta.productId) || delta.price),
                  reference_type: 'sale',
                  reference_id: id,
                  notes: `Sale Edit ${saleInvoiceNo} - ${delta.name}${delta.variationId ? ' (Variation)' : ''} - Delta: ${delta.deltaQty > 0 ? 'Restore' : 'Reduce'}`,
                  created_by: updateCreatedByAuthId,
                });

                if (!movement || !movement.id) {
                  throw new Error(`Stock movement creation returned null for product ${delta.productId}`);
                }
                console.log('[SALES CONTEXT] ✅ Stock movement created for delta:', delta.name, 'Movement ID:', movement.id, 'Qty:', movementQty);
              }
            } catch (movementError: any) {
              const errorMsg = `Failed to create stock movement for ${delta.name}: ${movementError.message || movementError}`;
              console.error('[SALES CONTEXT] ❌', errorMsg);
              stockMovementErrors.push(errorMsg);
            }
          }
          
          if (stockMovementErrors.length > 0) {
            console.error('[SALES CONTEXT] ⚠️ Some stock movements failed:', stockMovementErrors);
            toast.warning(`Sale updated but ${stockMovementErrors.length} stock movement(s) failed. Check console for details.`);
          } else {
            console.log('[SALES CONTEXT] ✅ All stock movement deltas created successfully for sale:', id, 'Deltas:', stockMovementDeltas.length);
          }
          
          // Dispatch event to refresh inventory
          window.dispatchEvent(new CustomEvent('saleSaved', { 
            detail: { saleId: id } 
          }));
          const custId = sale?.customerId || (sale as any)?.customer_id;
          if (custId) {
            window.dispatchEvent(new CustomEvent('ledgerUpdated', { detail: { ledgerType: 'customer', entityId: custId } }));
          }
        } catch (stockError: any) {
          console.error('[SALES CONTEXT] ❌ Stock movement delta creation error:', stockError);
          toast.warning('Sale updated but stock movements failed. Check console for details.');
          // Don't block update if stock movement fails
        }
      } else if (stockMovementDeltas.length === 0 && allowStockItemDeltas && (updates as any).items) {
        console.log('[SALES CONTEXT] ✅ No stock movement delta (items unchanged or no delta)');
      }

      // Order/Draft/Quotation → Final: create stock OUT for all items when only status is being updated (no item deltas)
      const wasNotFinal = sale != null && !canPostStockForSaleStatus(sale.status);
      const isNowFinal =
        newStatusForGate != null && canPostStockForSaleStatus(newStatusForGate);
      const effectiveCompanyId = companyId || (sale as any)?.company_id;
      if (wasNotFinal && isNowFinal && stockMovementDeltas.length === 0 && effectiveCompanyId) {
        try {
          const { supabase: sb } = await import('@/lib/supabase');
          let currentItems: {
            product_id: string;
            variation_id?: string;
            quantity: number;
            unit_price?: number;
            product_name?: string;
            bespoke_parent_item_id?: string | null;
            customization_details?: unknown;
            product_sku?: string | null;
            track_stock?: boolean | null;
          }[] = [];
          const { data: itemsFromSalesItems, error: itemsErr } = await sb
            .from('sales_items')
            .select(
              'product_id, variation_id, quantity, unit_price, product_name, customization_details, bespoke_parent_item_id, product:products(sku, track_stock)',
            )
            .eq('sale_id', id);
          if (!itemsErr && itemsFromSalesItems?.length) {
            currentItems = (itemsFromSalesItems as any[]).map((row) => ({
              product_id: row.product_id,
              variation_id: row.variation_id,
              quantity: row.quantity,
              unit_price: row.unit_price,
              product_name: row.product_name,
              bespoke_parent_item_id: row.bespoke_parent_item_id,
              customization_details: row.customization_details,
              product_sku: row.product?.sku ?? null,
              track_stock: row.product?.track_stock,
            }));
          }
          if (currentItems.length > 0) {
            let customGenericProductIds: string[] | null = null;
            try {
              const { data: bs } = await sb
                .from('business_settings')
                .select('custom_generic_product_ids')
                .eq('company_id', effectiveCompanyId)
                .maybeSingle();
              customGenericProductIds =
                (bs as { custom_generic_product_ids?: string[] } | null)?.custom_generic_product_ids ?? null;
            } catch {
              /* ignore */
            }
            const productMap = new Map(
              currentItems.map((row) => [
                row.product_id,
                { sku: row.product_sku, track_stock: row.track_stock },
              ]),
            );
            const stockEligibleItems = filterSaleLinesForStockPosting(
              currentItems,
              productMap,
              customGenericProductIds,
            );
            const { data: { user: authUser } } = await sb.auth.getUser();
            const updateCreatedByAuthId = authUser?.id ?? (user as any)?.auth_user_id ?? user?.id;
            const saleForStock = await saleService.getSaleById(id);
            const saleInvoiceNo =
              getSaleDisplayNumber(saleForStock as any) ||
              String((saleForStock as any)?.invoice_no ?? (saleForStock as any)?.invoiceNo ?? id);
            const saleBranchId = (saleForStock as any)?.branch_id ?? null;
            let effectiveBranchId = isValidBranchId(saleBranchId) ? saleBranchId : (isValidBranchId(branchId) ? branchId : null);
            if (!effectiveBranchId) {
              const branches = await branchService.getAllBranches(effectiveCompanyId);
              effectiveBranchId = branches?.length ? branches[0].id : null;
            }
            if (effectiveBranchId === 'all') effectiveBranchId = null;
            // Fetch weighted average cost from stock_movements (actual purchase cost)
            const statusChangeProductIds = stockEligibleItems.map((i) => i.product_id).filter(Boolean);
            const statusCostMap = new Map<string, number>();
            if (statusChangeProductIds.length > 0 && effectiveCompanyId) {
              const { data: movements } = await sb
                .from('stock_movements')
                .select('product_id, quantity, unit_cost, total_cost')
                .eq('company_id', effectiveCompanyId)
                .in('product_id', statusChangeProductIds)
                .in('movement_type', ['purchase', 'opening_stock']);
              const costAcc = new Map<string, { sum: number; qty: number }>();
              for (const m of movements || []) {
                const pid = (m as any).product_id;
                const mQty = Math.abs(Number((m as any).quantity) || 0);
                const mCost = Math.abs(Number((m as any).total_cost) || (mQty * (Number((m as any).unit_cost) || 0)));
                const acc = costAcc.get(pid) || { sum: 0, qty: 0 };
                acc.sum += mCost;
                acc.qty += mQty;
                costAcc.set(pid, acc);
              }
              for (const [pid, acc] of costAcc) {
                if (acc.qty > 0) statusCostMap.set(pid, acc.sum / acc.qty);
              }
              const missingIds = statusChangeProductIds.filter((id: string) => !statusCostMap.has(id));
              if (missingIds.length > 0) {
                const { data: fallback } = await sb.from('products').select('id, cost_price').in('id', missingIds);
                for (const r of fallback || []) {
                  if (!statusCostMap.has(r.id)) statusCostMap.set(r.id, Number((r as any).cost_price) || 0);
                }
              }
            }
            const { count: existingSaleMovCount } = await sb
              .from('stock_movements')
              .select('id', { count: 'exact', head: true })
              .eq('reference_type', 'sale')
              .eq('reference_id', id)
              .ilike('movement_type', 'sale');
            if ((existingSaleMovCount ?? 0) > 0) {
              console.log(
                '[SALES CONTEXT] Order→Final: stock already posted (trigger), skipping duplicate OUT',
                { saleId: id, existingSaleMovCount },
              );
            } else {
              for (const item of stockEligibleItems) {
                const qty = Number(item.quantity) || 0;
                if (qty <= 0) continue;
                const productId = item.product_id;
                const variationId = item.variation_id || undefined;
                await productService.createStockMovement({
                  company_id: effectiveCompanyId,
                  branch_id: effectiveBranchId ?? undefined,
                  product_id: productId,
                  variation_id: variationId,
                  movement_type: 'sale',
                  quantity: -qty,
                  unit_cost: statusCostMap.get(productId) || Number(item.unit_price) || 0,
                  reference_type: 'sale',
                  reference_id: id,
                  notes: `Sale ${saleInvoiceNo} – ${(item as any).product_name || 'Item'}`,
                  created_by: updateCreatedByAuthId ?? undefined,
                });
              }
              console.log(
                '[SALES CONTEXT] ✅ Stock OUT created for Order→Final (no item change):',
                stockEligibleItems.length,
                'items',
              );
            }
            window.dispatchEvent(new CustomEvent('saleSaved', { detail: { saleId: id } }));
            const custId = sale?.customerId ?? (saleForStock as any)?.customer_id;
            if (custId) window.dispatchEvent(new CustomEvent('ledgerUpdated', { detail: { ledgerType: 'customer', entityId: custId } }));
          }
        } catch (orderToFinalErr: any) {
          console.error('[SALES CONTEXT] Order→Final stock movements error:', orderToFinalErr);
          toast.warning('Sale marked Final but some stock movements failed. Check stock ledger.');
        }
      }

      // Bespoke fabric stock posts on work order complete (RPC), not on sale finalize.

      // Line-level charges: only replace when form explicitly saves or non-empty payload (never wipe on empty [] by mistake)
      const extraExpensesList = (updates as { extraExpenses?: { type?: string; amount?: number }[] }).extraExpenses;
      const updateShippingCharges = Number((updates as { shippingCharges?: number }).shippingCharges ?? 0);
      const replaceSaleChargesFlag = (updates as { replaceSaleCharges?: boolean }).replaceSaleCharges === true;
      const hasExtraLines =
        Array.isArray(extraExpensesList) &&
        extraExpensesList.some((e) => Number(e?.amount ?? 0) > 0);
      const shouldReplaceCharges =
        replaceSaleChargesFlag || hasExtraLines || updateShippingCharges > 0;
      if (shouldReplaceCharges) {
        const chargeOnBill = isChargeExtrasOnCustomerBill(updates as object);
        const discountAmt = Number(
          (updates as { discount?: number }).discount ??
            (updates as { discount_amount?: number }).discount_amount ??
            0,
        );
        const charges = await buildSaleChargeRowsFromExtras(
          companyId,
          extraExpensesList as SaleExtraExpenseInput[] | undefined,
          chargeOnBill,
          updateShippingCharges,
          discountAmt,
        );
        await saleService.replaceSaleCharges(id, charges, user?.id ?? undefined);
      }

      // PF-03: Sync shipping charge to sale_shipments so trigger updates sales.shipment_charges and due_amount (single source of truth)
      if ((updates as any).shippingCharges !== undefined && companyId) {
        try {
          const shipments = await shipmentService.getBySaleId(id);
          let effectiveBranchId = isValidBranchId(branchId) ? branchId : (sale?.location ?? null);
          if (!effectiveBranchId && companyId) {
            const branches = await branchService.getAllBranches(companyId);
            effectiveBranchId = branches?.length ? branches[0].id : null;
          }
          if (shipments.length > 0) {
            await shipmentService.update(shipments[0].id, { charged_to_customer: updateShippingCharges }, user?.id ?? undefined);
            console.log('[SALES CONTEXT] PF-03: Updated first shipment charged_to_customer, trigger will sync sale shipment_charges and due_amount');
          } else if (updateShippingCharges > 0 && effectiveBranchId) {
            await shipmentService.create(
              id,
              companyId,
              effectiveBranchId,
              { shipment_type: 'Courier', charged_to_customer: updateShippingCharges, actual_cost: 0, currency: 'PKR', shipment_status: 'Pending' },
              user?.id ?? undefined,
              sale?.invoiceNo ?? undefined
            );
            console.log('[SALES CONTEXT] PF-03: Created shipment for shipping charge, trigger will sync sale shipment_charges and due_amount');
          }
        } catch (shipSyncErr: any) {
          console.warn('[SALES CONTEXT] PF-03: Shipment sync for shipping charge failed:', shipSyncErr?.message);
        }
      }

      // PF-14 v2: In-place update — modify original document JE lines directly instead of creating adjustment JEs.
      if (accountingRepostNeeded && companyId && oldAccountingSnapshot) {
        try {
          const { supabase: sbEdit } = await import('@/lib/supabase');
          const updatedSale = await saleService.getSaleById(id);
          if (!updatedSale) throw new Error('Sale not found after update');
          const { saleAccountingService: sac } = await import('@/app/services/saleAccountingService');
          const newSnapshot = sac.getSaleAccountingSnapshot(updatedSale);
          const invoiceNo = (updatedSale as any).invoice_no ?? (updatedSale as any).invoiceNo ?? id;

          // Find the original sale document JE (not payment, not adjustment, not return)
          const { data: docJe } = await sbEdit
            .from('journal_entries')
            .select('id, description')
            .eq('company_id', companyId)
            .eq('reference_type', 'sale')
            .eq('reference_id', id)
            .is('payment_id', null)
            .or('is_void.is.null,is_void.eq.false')
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();

          if (docJe?.id) {
            const jeId = docJe.id as string;
            const newTotal = newSnapshot.total;
            const newDiscount = newSnapshot.discount;
            const newShipping = newSnapshot.shippingCharges || 0;
            const newExtra = newSnapshot.extraExpense || 0;

            // Resolve AR sub-ledger for customer
            const customerId = (updatedSale as any).customer_id;
            let arAccountId: string | null = null;
            if (customerId) {
              const { resolveReceivablePostingAccountId } = await import('@/app/services/partySubledgerAccountService');
              arAccountId = await resolveReceivablePostingAccountId(companyId, customerId);
            }
            if (!arAccountId) {
              const { data: arAcct } = await sbEdit.from('accounts').select('id').eq('code', '1100').eq('company_id', companyId).maybeSingle();
              arAccountId = arAcct?.id as string | null;
            }

            // Account IDs must match saleAccountingService.createSaleJournalEntry (4000 revenue, 5010 COGS, optional 4010 studio).
            const getAccId = async (code: string) => {
              const { data } = await sbEdit.from('accounts').select('id').eq('code', code).eq('company_id', companyId).eq('is_active', true).maybeSingle();
              return data?.id as string | null;
            };
            const {
              computeProductRevenueCreditSplit,
              computeSaleDocumentRevenueAmounts,
              syncSaleDocumentJeCogsInventoryPair,
              assertJournalEntryBalanced,
            } = await import('@/app/services/saleAccountingService');
            const docAmounts = computeSaleDocumentRevenueAmounts({
              total: newTotal,
              discount: newDiscount,
              extraExpense: newExtra,
              shippingCharges: newShipping,
            });
            const revenueSplit = await computeProductRevenueCreditSplit(
              id,
              Math.max(0, docAmounts.merchandisePool)
            );
            const merchandiseRevenueIds = new Set(
              [(await getAccId('4000')), (await getAccId('4100'))].filter(Boolean) as string[]
            );
            const studioRevenueId = await getAccId('4010');
            const discountId = await getAccId('5200');
            const shippingIncomeId = await getAccId('4110');
            const extraServiceIncomeId = await getAccId('4120');
            const ar1100Id = await getAccId('1100');

            const { data: jeLines } = await sbEdit.from('journal_entry_lines').select('id, account_id, debit, credit').eq('journal_entry_id', jeId);

            let merchandiseCreditAssigned = false;

            // Update each line based on its account
            for (const line of (jeLines || []) as any[]) {
              const accId = line.account_id;
              let newDebit = 0;
              let newCredit = 0;
              let didMatch = false;

              if (accId === arAccountId || (ar1100Id && accId === ar1100Id)) {
                newDebit = docAmounts.arDebit;
                newCredit = 0;
                didMatch = true;
              } else if (merchandiseRevenueIds.has(accId)) {
                newDebit = 0;
                if (!merchandiseCreditAssigned) {
                  newCredit = revenueSplit.merchandiseCredit;
                  merchandiseCreditAssigned = true;
                } else {
                  newCredit = 0;
                }
                didMatch = true;
              } else if (studioRevenueId && accId === studioRevenueId) {
                newDebit = 0;
                newCredit = revenueSplit.studioServiceCredit;
                didMatch = true;
              } else if (discountId && accId === discountId) {
                newDebit = newDiscount > 0 ? newDiscount : 0;
                newCredit = 0;
                didMatch = true;
              } else if (shippingIncomeId && accId === shippingIncomeId) {
                newDebit = 0;
                newCredit = newShipping;
                didMatch = true;
              } else if (extraServiceIncomeId && accId === extraServiceIncomeId) {
                newDebit = 0;
                newCredit = newExtra;
                didMatch = true;
              } else {
                continue;
              }

              if (didMatch) {
                await sbEdit.from('journal_entry_lines').update({ debit: newDebit, credit: newCredit }).eq('id', line.id);
              }
            }

            await syncSaleDocumentJeCogsInventoryPair({
              journalEntryId: jeId,
              saleId: id,
              companyId,
              invoiceNo,
            });

            // Handle discount line: add if new, remove if zero
            if (newDiscount > 0 && discountId) {
              const { data: discLine } = await sbEdit.from('journal_entry_lines').select('id').eq('journal_entry_id', jeId).eq('account_id', discountId).maybeSingle();
              if (!discLine) {
                await sbEdit.from('journal_entry_lines').insert({ journal_entry_id: jeId, account_id: discountId, debit: newDiscount, credit: 0, description: `Discount – ${invoiceNo}` });
              }
            }

            // Handle extra service income line: add if new extra > 0 and line missing
            if (newExtra > 0 && extraServiceIncomeId) {
              const { data: extraLine } = await sbEdit.from('journal_entry_lines').select('id').eq('journal_entry_id', jeId).eq('account_id', extraServiceIncomeId).maybeSingle();
              if (!extraLine) {
                await sbEdit.from('journal_entry_lines').insert({
                  journal_entry_id: jeId,
                  account_id: extraServiceIncomeId,
                  debit: 0,
                  credit: newExtra,
                  description: `Extra Service Income – ${invoiceNo}`,
                });
              }
            }

            // Log edit in JE description
            const ts = new Date().toLocaleString('en-PK', { dateStyle: 'short', timeStyle: 'short' });
            const oldTotal = oldAccountingSnapshot.total;
            const editLog = `[Edited ${ts}: Total Rs ${oldTotal.toLocaleString()} → Rs ${newTotal.toLocaleString()}]`;
            await sbEdit.from('journal_entries').update({ description: `${docJe.description || ''} ${editLog}`.slice(0, 500) }).eq('id', jeId);

            const balanceCheck = await assertJournalEntryBalanced(jeId);
            if (!balanceCheck.balanced) {
              console.warn(
                '[SALES CONTEXT] PF-14 v2: JE out of balance after in-place edit (diff',
                balanceCheck.diff,
                ') — rebuilding sale document accounting for',
                invoiceNo
              );
              const { rebuildSaleDocumentAccounting } = await import('@/app/services/documentPostingEngine');
              await rebuildSaleDocumentAccounting(id);
            } else {
              console.log('[SALES CONTEXT] PF-14 v2: In-place updated sale document JE', jeId, 'for', invoiceNo);
            }
            dispatchSaleLifecycleInvalidated({
              companyId,
              branchId: branchId ?? null,
              saleId: id,
              reason: 'saleDocumentJeEdited',
            });
          }

          // Phase 4: Activity only when value actually changed (no misleading or duplicate entries)
          const logComponent = (field: string, oldVal: number, newVal: number, label: string) => {
            if (oldVal === newVal) return;
            activityLogService.logActivity({
              companyId,
              module: 'sale',
              entityId: id,
              entityReference: invoiceNo,
              action: 'sale_component_edited',
              field: label,
              oldValue: oldVal,
              newValue: newVal,
              performedBy: createdByAuthId ?? undefined,
              description: `${label} changed from Rs ${Number(oldVal).toLocaleString()} to Rs ${Number(newVal).toLocaleString()}`,
            }).catch((e) => console.warn('[SALES CONTEXT] Activity log sale_component_edited failed:', e));
          };
          logComponent('discount', oldAccountingSnapshot.discount, newSnapshot.discount, 'Discount');
          logComponent('shipping', oldAccountingSnapshot.shippingCharges, newSnapshot.shippingCharges, 'Shipping');
          logComponent('extra_expense', oldAccountingSnapshot.extraExpense, newSnapshot.extraExpense, 'Extra expense');
          const oldTotal = oldAccountingSnapshot.total;
          const newTotal = newSnapshot.total;
          if (oldTotal !== newTotal) {
            activityLogService.logActivity({
              companyId,
              module: 'sale',
              entityId: id,
              entityReference: invoiceNo,
              action: 'sale_component_edited',
              field: 'Total',
              oldValue: oldTotal,
              newValue: newTotal,
              performedBy: (user as any)?.id ?? undefined,
              description: `Total changed from Rs ${Number(oldTotal).toLocaleString()} to Rs ${Number(newTotal).toLocaleString()}`,
            }).catch((e) => console.warn('[SALES CONTEXT] Activity log sale total failed:', e));
          }
        } catch (adjErr: any) {
          console.error('[SALES CONTEXT] PF-14: Sale edit adjustment posting failed:', adjErr);
          toast.warning('Sale updated, but accounting adjustments failed. Check logs.');
        }
      }

      // Commission: NOT posted per sale on update. Batch posting only via Commission Report.

      // 🔒 GOLDEN RULE: Payment MUST go to payments table, NEVER directly update sale.paid_amount
      // If updates.paid is provided, create/update payment record in payments table
      // Database trigger will automatically update sale.paid_amount from payments table
      if (updates.paid !== undefined) {
        try {
          const saleForSync = getSaleById(id);
          const nextStatusRaw =
            updates.status !== undefined
              ? (updates.status === 'invoice' ? 'final' : String(updates.status))
              : saleForSync?.status;
          if (!saleForSync || !companyId) {
            console.warn('[SALES CONTEXT] Cannot sync payment: sale or company missing');
          } else if (!canPostAccountingForSaleStatus(nextStatusRaw)) {
            if (Number(updates.paid) > 0) {
              console.warn(
                '[SALES CONTEXT] Skipping payment sync: only posted (final) sales may create payments / payment JEs.',
                { id, nextStatusRaw }
              );
            }
          } else {
            const existingPayments = await saleService.getSalePayments(id);
            const paidAmount = Number(updates.paid) || 0;
            const paymentMethod = updates.paymentMethod || saleForSync?.paymentMethod || 'Cash';
            
            let effectiveBranchId = isValidBranchId(branchId) ? branchId : null;
            if (!effectiveBranchId && companyId) {
              const branches = await branchService.getAllBranches(companyId);
              effectiveBranchId = branches?.length ? branches[0].id : null;
            }
            
            if (companyId && effectiveBranchId && user) {
              const { accountHelperService } = await import('@/app/services/accountHelperService');
              const { accountService } = await import('@/app/services/accountService');
              const normalizedMethod = normalizePaymentMethodForEnum(paymentMethod);
              
              // Get payment account ID
              let paymentAccountId = await accountHelperService.getDefaultAccountByPaymentMethod(normalizedMethod, companyId);
              if (!paymentAccountId) {
                const allAccounts = await accountService.getAllAccounts(companyId);
                paymentAccountId = allAccounts?.find((acc: any) => acc.code === '1000')?.id || null;
              }
              
              if (!paymentAccountId) {
                console.error('[SALES CONTEXT] No payment account found for', normalizedMethod);
                throw new Error(`No payment account found for ${normalizedMethod}. Please create a default account.`);
              }
              
              // Phase 3: Only touch payment when paid or account actually changed (document-only edit must not repost payment)
              const normPayAcct = (u: string | null | undefined) =>
                u && String(u).trim() ? String(u).replace(/-/g, '').toLowerCase() : '';
              const payAcctSame =
                normPayAcct(existingPayments?.[0]?.payment_account_id) === normPayAcct(paymentAccountId) ||
                !existingPayments?.[0]?.payment_account_id;
              const paymentUnchanged =
                existingPayments.length === 1 &&
                Math.round((paidAmount - (Number(existingPayments[0].amount) || 0)) * 100) === 0 &&
                payAcctSame;

              if (existingPayments.length === 0 && paidAmount > 0) {
                await saleService.recordPayment(
                  id,
                  paidAmount,
                  paymentMethod,
                  paymentAccountId,
                  companyId,
                  effectiveBranchId,
                  saleForSync.date,
                  undefined
                );
                console.log('[SALES CONTEXT] ✅ Payment record created in payments table');
              } else if (existingPayments.length === 1 && !paymentUnchanged) {
                const { tracePaymentEditFlow } = await import('@/app/lib/paymentEditFlowTrace');
                tracePaymentEditFlow('SalesContext.sync_sale_payment.updatePayment', {
                  paymentId: existingPayments[0].id,
                  saleId: id,
                  amount: paidAmount,
                  accountId: paymentAccountId,
                });
                await saleService.updatePayment(
                  existingPayments[0].id,
                  id,
                  {
                    amount: paidAmount,
                    paymentMethod: normalizedMethod,
                    accountId: paymentAccountId,
                  }
                );
                console.log('[SALES CONTEXT] ✅ Payment record updated in payments table');
              } else if (existingPayments.length === 1 && paymentUnchanged) {
                if (import.meta.env?.DEV) console.log('[SALES CONTEXT] Phase 3: Payment unchanged, skipping update');
              } else if (existingPayments.length > 1) {
                console.warn('[SALES CONTEXT] Multiple payments exist, cannot auto-update. Use payment management UI.');
              }
            }
          }
        } catch (syncErr: any) {
          console.error('[SALES CONTEXT] ❌ Payment sync failed:', syncErr);
          // Don't block sale update if payment sync fails, but log error
          toast.error(`Payment sync failed: ${syncErr.message || 'Unknown error'}`);
        }
      }

      // CRITICAL FIX: Handle payments if provided in updates
      // When editing, existing payments should NOT be recreated
      // Only new payments (not marked as existing) should be created
      if ((updates as any).partialPayments && Array.isArray((updates as any).partialPayments)) {
        const partialPayments = (updates as any).partialPayments;
        const newPayments = partialPayments.filter((p: any) => !p.isExisting);
        let updateBranchId = isValidBranchId(branchId) ? branchId : null;
        if (!updateBranchId && companyId) {
          const branches = await branchService.getAllBranches(companyId);
          updateBranchId = branches?.length ? branches[0].id : null;
        }
        if (newPayments.length > 0 && companyId && updateBranchId && user) {
          try {
            const { accountHelperService } = await import('@/app/services/accountHelperService');
            const { saleService } = await import('@/app/services/saleService');
            const { accountService } = await import('@/app/services/accountService');
            
            for (const partialPayment of newPayments) {
              const paymentMethod = normalizePaymentMethodForEnum(partialPayment.method || 'cash');
              
              // Get account for this payment method
              let paymentAccountId = await accountHelperService.getDefaultAccountByPaymentMethod(
                paymentMethod,
                companyId
              );
              
              // If no account found, use Cash as default
              if (!paymentAccountId) {
                const allAccounts = await accountService.getAllAccounts(companyId);
                const cashAccount = allAccounts.find(acc => acc.code === '1000');
                paymentAccountId = cashAccount?.id || null;
              }
              
              if (paymentAccountId && partialPayment.amount > 0) {
                await saleService.recordPayment(
                  id,
                  partialPayment.amount,
                  paymentMethod,
                  paymentAccountId,
                  companyId,
                  updateBranchId,
                  undefined,
                  undefined
                );
                // Create separate journal entry for this payment
                accounting?.recordSalePayment({
                  saleId: id,
                  invoiceNo: sale?.invoiceNo || '',
                  customerName: sale?.customerName || '',
                  amount: partialPayment.amount,
                  paymentMethod: paymentMethod as any,
                  accountId: paymentAccountId,
                });
              }
            }
          } catch (error: any) {
            console.error('[SALES CONTEXT] Error recording new payments during edit:', error);
            // Don't fail update if payment recording fails
          }
        }
      }
      
      // Z1 + commission + list patch: one post-mutation fetch (was 3× getSaleById)
      let postUpdateSaleRow: Awaited<ReturnType<typeof saleService.getSaleById>> | null = null;
      try {
        postUpdateSaleRow = await saleService.getSaleById(id);
        const syncSt = String((postUpdateSaleRow as any)?.status ?? '').toLowerCase();
        if (postUpdateSaleRow && (canPostStockForSaleStatus(syncSt) || syncSt === 'cancelled')) {
          const { syncSaleStockForDocument } = await import('@/app/services/documentStockSyncService');
          const z1 = await syncSaleStockForDocument(id);
          if (z1.adjustmentsInserted > 0) {
            console.log('[SALES CONTEXT] Z1 stock sync (update):', z1.keysAdjusted);
            window.dispatchEvent(new CustomEvent('saleSaved', { detail: { saleId: id } }));
          }
        }
      } catch (z1Err) {
        console.warn('[SALES CONTEXT] Z1 stock sync (update) failed:', z1Err);
      }

      // Commission on final: pending row until Commission Report batch post
      try {
        const rowForCommission = postUpdateSaleRow ?? (await saleService.getSaleById(id));
        postUpdateSaleRow = rowForCommission;
        const st = String((rowForCommission as { status?: string }).status ?? '').toLowerCase();
        const isFinalNow = canPostAccountingForSaleStatus(st);
        if (isFinalNow && sale) {
          const wasFinal = canPostAccountingForSaleStatus(sale.status);
          const becameFinal = !wasFinal;
          const commissionTouched =
            (updates as { salesmanId?: unknown }).salesmanId !== undefined ||
            (updates as { commissionPercent?: unknown }).commissionPercent !== undefined ||
            (updates as { commissionAmount?: unknown }).commissionAmount !== undefined;
          const subtotalChanged = updates.subtotal !== undefined;
          const rowSalesman = (rowForCommission as { salesman_id?: string | null }).salesman_id;
          const needsCommission =
            becameFinal ||
            commissionTouched ||
            (wasFinal && subtotalChanged) ||
            (becameFinal && !rowSalesman);

          if (needsCommission) {
            const { supabase: sb } = await import('@/lib/supabase');
            let profileUserId: string | null = null;
            if (user?.id) {
              const { data: prof } = await sb
                .from('users')
                .select('id')
                .or(`id.eq.${user.id},auth_user_id.eq.${user.id}`)
                .maybeSingle();
              profileUserId = (prof as { id?: string } | null)?.id ?? null;
            }
            const subtotal =
              Number(updates.subtotal ?? (rowForCommission as { subtotal?: number }).subtotal) || 0;
            let explicitSid: string | null | undefined;
            if ((updates as { salesmanId?: string }).salesmanId !== undefined) {
              const sid = (updates as { salesmanId?: string }).salesmanId;
              explicitSid = sid === '' || sid === 'none' || sid === '1' ? null : sid;
            } else {
              explicitSid = rowSalesman ?? undefined;
            }
            const { resolveSaleCommissionFields, applySaleCommissionPatch } = await import(
              '@/app/lib/saleCommission'
            );
            const patch = await resolveSaleCommissionFields({
              profileUserId,
              subtotal,
              actorRole: userRole,
              explicitSalesmanId: explicitSid,
              explicitCommissionPercent:
                (updates as { commissionPercent?: number | null }).commissionPercent !== undefined
                  ? (updates as { commissionPercent?: number | null }).commissionPercent
                  : (rowForCommission as { commission_percent?: number | null }).commission_percent,
            });
            if (patch) {
              const commErr = await applySaleCommissionPatch(id, patch);
              if (commErr) console.warn('[SALES CONTEXT] Commission patch:', commErr);
              // Commission patch may change row — refresh once for list state
              postUpdateSaleRow = await saleService.getSaleById(id);
            }
          }
        }
      } catch (commEx) {
        console.warn('[SALES CONTEXT] Commission on final failed:', commEx);
      }

      // CRITICAL FIX: Reload sale from database to get fresh data
      const updatedSaleData = postUpdateSaleRow ?? (await saleService.getSaleById(id));
      if (updatedSaleData) {
        const updatedSale = convertFromSupabaseSale(updatedSaleData);
        setSales(prev => prev.map(s => s.id === id ? updatedSale : s));
      } else {
        // Fallback: Update local state
      setSales(prev => prev.map(sale => 
        sale.id === id 
          ? { ...sale, ...updates, updatedAt: getCurrentLocalTimestamp() }
          : sale
      ));
      }
      if (companyId && updatedSaleData) {
        dispatchSaleLifecycleInvalidated({
          companyId,
          branchId: (updatedSaleData as any).branch_id ?? null,
          customerId: (updatedSaleData as any).customer_id ?? sale?.customer ?? null,
          saleId: id,
          reason: 'sales-context-update',
        });
      }
      // Activity log: detail of what changed (use sale from start of update, before state refresh)
      if (companyId && user?.id && sale) {
        const changes: string[] = [];
        if (updates.total !== undefined && Number(sale.total) !== Number(updates.total)) {
          changes.push(`Total changed from Rs ${Number(sale.total).toLocaleString()} to Rs ${Number(updates.total).toLocaleString()}`);
        }
        if (updates.discount !== undefined && Number(sale.discount) !== Number(updates.discount)) {
          changes.push(`Discount from Rs ${Number(sale.discount).toLocaleString()} to Rs ${Number(updates.discount).toLocaleString()}`);
        }
        if (updates.expenses !== undefined && Number(sale.expenses) !== Number(updates.expenses)) {
          changes.push(`Expenses from Rs ${Number(sale.expenses).toLocaleString()} to Rs ${Number(updates.expenses).toLocaleString()}`);
        }
        if (updates.status !== undefined && sale.status !== updates.status) {
          changes.push(`Status from ${sale.status} to ${updates.status}`);
        }
        if ((updates as any).items && Array.isArray((updates as any).items) && (sale.itemsCount ?? sale.items?.length) !== (updates as any).items?.length) {
          changes.push(`Items count from ${sale.itemsCount ?? sale.items?.length ?? 0} to ${(updates as any).items.length}`);
        }
        if (updates.notes !== undefined && sale.notes !== updates.notes) changes.push('Notes updated');
        const description = changes.length > 0 ? changes.join('; ') : 'Sale updated';
        activityLogService.logActivity({
          companyId,
          module: 'sale',
          entityId: id,
          entityReference: sale.invoiceNo,
          action: 'update',
          performedBy: user.id,
          description,
        }).catch((err) => console.warn('[SALES CONTEXT] Activity log failed:', err));
      }

      const studioDesignPatch = (updates as any).studioDesignName;
      if (studioDesignPatch !== undefined && companyId) {
        const trimmed = String(studioDesignPatch).trim();
        try {
          const rowSale = await saleService.getSaleById(id);
          const isStudioRow =
            (rowSale as any)?.is_studio === true ||
            String((rowSale as any)?.order_no ?? '')
              .toUpperCase()
              .startsWith('STD-') ||
            String((rowSale as any)?.order_no ?? '')
              .toUpperCase()
              .startsWith('ST-');
          if (isStudioRow) {
            if (!trimmed) {
              throw new Error('Studio product name is required.');
            }
            const { supabase: sb } = await import('@/lib/supabase');
            const { data: existingProd } = await sb
              .from('studio_productions')
              .select('id')
              .eq('sale_id', id)
              .maybeSingle();
            const branchIdRow = (rowSale as any)?.branch_id as string | undefined;
            const productionNo =
              String((rowSale as any)?.order_no || (rowSale as any)?.invoice_no || '').trim() || `STD-${id.slice(0, 8)}`;
            if (existingProd?.id) {
              await sb.from('studio_productions').update({ design_name: trimmed }).eq('id', existingProd.id);
            } else {
              const { data: saleItemsRows } = await sb
                .from('sales_items')
                .select('product_id, variation_id, quantity')
                .eq('sale_id', id);
              const si = saleItemsRows || [];
              const first = si[0];
              if (first?.product_id) {
                const qtySum = si.reduce((s: number, it: any) => s + (Number(it.quantity) || 0), 0);
                const qty = qtySum > 0 ? qtySum : Number(first.quantity) || 1;
                await sb.from('studio_productions').insert({
                  company_id: companyId,
                  branch_id: branchIdRow,
                  sale_id: id,
                  production_no: productionNo,
                  production_date: localNowDateString(),
                  product_id: first.product_id,
                  variation_id: first.variation_id || null,
                  quantity: qty,
                  status: 'draft',
                  created_by: user?.id ?? undefined,
                  design_name: trimmed,
                });
              }
            }
          }
        } catch (studioSyncErr: any) {
          console.error('[SALES CONTEXT] Studio production design sync on update failed:', studioSyncErr);
          throw studioSyncErr;
        }
      }

      // POS updates show their own toast; avoid duplicate for POS invoices
      const isPOS = getSaleById(id)?.invoiceNo?.startsWith('POS-');
      if (!isPOS) toast.success('Sale updated successfully!');
      // Notify views (e.g. Studio Sale Detail, Studio Sales List) to refetch so UI shows updated data
      window.dispatchEvent(new CustomEvent('saleUpdated', { detail: { saleId: id } }));
      pushAccountingEditTrace({
        traceId,
        ts: getCurrentLocalTimestamp(),
        module: 'sales',
        entityType: 'sale',
        entityId: id,
        companyId: companyId || null,
        branchId: branchId || null,
        phase: 'done',
      });
    } catch (error: any) {
      console.error('[SALES CONTEXT] Error updating sale:', error);
      pushAccountingEditTrace({
        traceId,
        ts: getCurrentLocalTimestamp(),
        module: 'sales',
        entityType: 'sale',
        entityId: id,
        companyId: companyId || null,
        branchId: branchId || null,
        phase: 'error',
        data: { message: error?.message || String(error) },
      });
      toast.error(`Failed to update sale: ${error.message || 'Unknown error'}`);
      throw error;
    }
  };

  // Delete sale
  const deleteSale = async (id: string): Promise<void> => {
    const sale = getSaleById(id);
    if (!sale) {
      throw new Error('Sale not found');
    }

    try {
      const rowBefore = await saleService.getSaleById(id);
      // CRITICAL FIX: Use hard delete since 'cancelled' status doesn't exist in enum
      // sale_status enum only supports: draft, quotation, order, final
      await saleService.deleteSale(id);
      
      // Update local state
      setSales(prev => prev.filter(s => s.id !== id));

      if (companyId) {
        dispatchSaleLifecycleInvalidated({
          companyId,
          branchId: (rowBefore as any)?.branch_id ?? null,
          customerId: (rowBefore as any)?.customer_id ?? sale.customer ?? null,
          saleId: id,
          reason: 'sales-context-delete',
        });
      }
      
      // CRITICAL: Dispatch event to refresh ledger views and accounting
      window.dispatchEvent(new CustomEvent('saleDeleted', { detail: { saleId: id } }));
      const custId = sale.customerId || (sale as any)?.customer;
      if (custId) {
        window.dispatchEvent(new CustomEvent('ledgerUpdated', { detail: { ledgerType: 'customer', entityId: custId } }));
      }
      
      toast.success(`${sale.invoiceNo} deleted successfully!`);
    } catch (error: any) {
      console.error('[SALES CONTEXT] Error deleting sale:', error);
      toast.error(`Failed to delete sale: ${error.message || 'Unknown error'}`);
      throw error;
    }
  };

  // Record payment
  const recordPayment = async (saleId: string, amount: number, method: string, accountId?: string): Promise<void> => {
    const sale = getSaleById(saleId);
    if (!sale || !companyId) {
      throw new Error('Sale not found or company missing');
    }
    let effectiveBranchId = isValidBranchId(branchId) ? branchId : null;
    if (!effectiveBranchId) {
      const branches = await branchService.getAllBranches(companyId);
      if (!branches?.length) throw new Error('No branch found. Please create at least one branch.');
      effectiveBranchId = branches[0].id;
    }

    try {
      // CRITICAL FIX: Get account ID from payment method if not provided
      let paymentAccountId = accountId;
      
      if (!paymentAccountId && companyId) {
        const { accountHelperService } = await import('@/app/services/accountHelperService');
        paymentAccountId = await accountHelperService.getDefaultAccountByPaymentMethod(
          method,
          companyId
        ) || null;
      }
      
      // If still no account, throw error
      if (!paymentAccountId) {
        throw new Error(`No account found for ${method} payment. Please create a default account.`);
      }
      
      // Record payment in Supabase; let DB trigger set reference_number (avoid duplicate key)
      await saleService.recordPayment(saleId, amount, method, paymentAccountId, companyId, effectiveBranchId, undefined, undefined);

      // CRITICAL FIX: Reload sale to get updated paid/due amounts from database (trigger updated them)
      // Don't manually calculate - let database trigger handle it
      const updatedSaleData = await saleService.getSaleById(saleId);
      if (updatedSaleData) {
        const updatedSale = convertFromSupabaseSale(updatedSaleData);
        setSales(prev => prev.map(s => s.id === saleId ? updatedSale : s));
      }

      if (companyId && updatedSaleData) {
        dispatchSaleLifecycleInvalidated({
          companyId,
          branchId: (updatedSaleData as any).branch_id ?? null,
          customerId: (updatedSaleData as any).customer_id ?? sale.customer ?? null,
          saleId,
          reason: 'sales-context-payment',
        });
      }

      // CRITICAL FIX: Create journal entry ONLY (payment already recorded above)
      // DO NOT call recordPayment again - it's already done
      accounting?.recordSalePayment({
        saleId: sale.id, // CRITICAL FIX: UUID for reference_id
        invoiceNo: sale.invoiceNo, // Invoice number for referenceNo
        customerName: sale.customerName,
        amount,
        paymentMethod: method as any,
        accountId: paymentAccountId, // CRITICAL: Pass account ID
      });

      window.dispatchEvent(
        new CustomEvent('saleUpdated', { detail: { saleId, fullReload: false } })
      );
      toast.success(`Payment of ${formatCurrency(amount)} recorded!`);
    } catch (error: any) {
      console.error('[SALES CONTEXT] Error recording payment:', error);
      toast.error(`Failed to record payment: ${error.message || 'Unknown error'}`);
      throw error;
    }
  };

  // Update shipping status
  const updateShippingStatus = async (saleId: string, status: ShippingStatus): Promise<void> => {
    const sale = getSaleById(saleId);
    if (!sale) {
      throw new Error('Sale not found');
    }

    try {
      await updateSale(saleId, { shippingStatus: status });
      
      // CRITICAL FIX: Update local state immediately for instant UI feedback
      setSales(prev => prev.map(s => 
        s.id === saleId ? { ...s, shippingStatus: status } : s
      ));
      
      // Also refresh from database in background
      void loadSales();
      
      toast.success(`Shipping status updated to ${status}!`);
    } catch (error) {
      throw error;
    }
  };

  // Convert quotation (or draft/order) to final invoice — same row: assign SL invoice_no + status final
  const convertQuotationToInvoice = async (quotationId: string): Promise<Sale> => {
    try {
      if (!companyId) throw new Error('Company required');
      const full = await saleService.getSaleById(quotationId);
      if (!full) throw new Error('Sale not found');
      const q = convertFromSupabaseSale(full);
      const nextSl = await documentNumberService.getNextDocumentNumberGlobal(companyId, 'SL');
      const charges = q.charges || [];
      const extraExpenses = charges
        .filter((c: any) => (c.charge_type || c.chargeType) !== 'shipping' && (c.charge_type || c.chargeType) !== 'discount')
        .map((c: any) => ({
          type: (c.charge_type || c.chargeType || 'other') as string,
          amount: Number(c.amount) || 0,
        }));
      const shippingFromCharges = charges
        .filter((c: any) => (c.charge_type || c.chargeType) === 'shipping')
        .reduce((s: number, c: any) => s + (Number(c.amount) || 0), 0);
      await updateSale(quotationId, {
        type: 'invoice',
        status: 'final',
        invoiceNo: nextSl,
        items: q.items,
        itemsCount: q.itemsCount,
        subtotal: q.subtotal,
        discount: q.discount,
        tax: q.tax,
        expenses: q.expenses,
        total: q.total,
        paid: q.paid,
        due: q.due,
        returnDue: q.returnDue,
        paymentStatus: q.paymentStatus,
        paymentMethod: q.paymentMethod,
        shippingStatus: q.shippingStatus as ShippingStatus,
        notes: q.notes,
        extraExpenses,
        replaceSaleCharges: true,
        shippingCharges: shippingFromCharges || q.shippingCharges || 0,
        partialPayments: [],
        is_studio: false,
        commissionAmount: q.commissionAmount,
        salesmanId: q.salesmanId,
        commissionPercent: q.commissionPercent,
      } as any);
      const refreshed = await saleService.getSaleById(quotationId);
      if (!refreshed) throw new Error('Sale not found after convert');
      const out = convertFromSupabaseSale(refreshed);
      setSales((prev) => prev.map((s) => (s.id === quotationId ? out : s)));
      toast.success(`Invoice ${out.invoiceNo} — same document finalized.`);
      return out;
    } catch (error: any) {
      console.error('[SALES CONTEXT] Error converting quotation:', error);
      toast.error(`Failed to convert: ${error.message || 'Unknown error'}`);
      throw error;
    }
  };

  const value = useMemo<SalesContextType>(() => ({
    sales,
    loading,
    totalCount,
    page,
    pageSize,
    setPage,
    setPageSize,
    listSearch,
    setListSearch,
    getSaleById,
    createSale,
    updateSale,
    deleteSale,
    recordPayment,
    updateShippingStatus,
    convertQuotationToInvoice,
    refreshSales: loadSales,
    __activate: activate,
  }), [
    sales, loading, totalCount, page, pageSize, setPage, setPageSize, listSearch, setListSearch,
    getSaleById, createSale, updateSale, deleteSale,
    recordPayment, updateShippingStatus, convertQuotationToInvoice, loadSales, activate,
  ]);

  return (
    <SalesContext.Provider value={value}>
      {children}
    </SalesContext.Provider>
  );
};
